import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useWallet, useConnection } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { Connection, PublicKey, Transaction, SystemProgram, LAMPORTS_PER_SOL, TransactionInstruction } from '@solana/web3.js';
import { Buffer } from 'buffer';
import { Link, useNavigate } from 'react-router-dom';
import { GAME_CONFIGS, WALLET_ADDRESSES } from '../../config/gameConfigs';
import { useWalletRegistration } from '../../hooks/useWalletRegistration';

const ORACLE_URL = process.env.REACT_APP_ORACLE_URL || 'http://localhost:3002';

const GAME_W = 900;
const GAME_H = 750;
const ROOSTER_Y = 120;
const ROOSTER_W = 60;
const FARMER_Y = 670;
const POOP_SPEED = 7;
const ROOSTER_SPEED = 7.92;

const ROUNDS = [
  { level: 1, weapon: 'rake',       label: 'Rake',       speed: 1.35, interval: 2200, count: 8  },
  { level: 2, weapon: 'hoe',        label: 'Hoe',        speed: 1.62, interval: 2000, count: 10 },
  { level: 3, weapon: 'shovel',     label: 'Shovel',     speed: 1.89, interval: 1800, count: 12 },
  { level: 4, weapon: 'pitch_fork', label: 'Pitchfork',  speed: 2.16, interval: 1600, count: 14 },
  { level: 5, weapon: 'pick_axe',   label: 'Pick Axe',   speed: 2.43, interval: 1400, count: 16 },
  { level: 6, weapon: 'shears',     label: 'Shears',     speed: 2.70, interval: 1200, count: 18 },
  { level: 7, weapon: 'axe',        label: 'Axe',        speed: 2.97, interval: 1100, count: 20 },
  { level: 8, weapon: 'scythe',     label: 'Scythe',     speed: 3.24, interval: 1000, count: 22 },
  { level: 9, weapon: 'sprayer',    label: 'Sprayer',    speed: 3.51, interval: 900,  count: 24 },
  { level: 10, weapon: 'mean_dog',  label: 'Mean Dog',   speed: 4.05, interval: 700,  count: 30 },
];

const ROUND_POINTS = [5000, 7500, 10000, 20000, 30000, 50000, 70000, 100000, 140000, 200000];

function Rooster({ x, pooping }) {
  return (
    <g transform={`translate(${x}, ${ROOSTER_Y})`}>
      {/* Body */}
      <ellipse cx="0" cy="0" rx="22" ry="18" fill="#ffffff" />
      <ellipse cx="-8" cy="4" rx="10" ry="8" fill="#e8e8e8" />
      {/* Tail feathers - white */}
      <polygon points="-22,-5 -35,-20 -28,0" fill="#ffffff" />
      <polygon points="-22,-2 -38,-12 -30,5" fill="#f0f0f0" />
      <polygon points="-22,2 -36,5 -28,12" fill="#ffffff" />
      <polygon points="-22,5 -32,15 -24,18" fill="#f0f0f0" />
      {/* Wing */}
      <ellipse cx="-4" cy="2" rx="14" ry="9" fill="#f0f0f0" />
      {/* Head */}
      <circle cx="18" cy="-10" r="13" fill="#ffffff" />
      {/* Comb - red spiky */}
      <polygon points="14,-22 17,-30 20,-22" fill="#c0392b" />
      <polygon points="18,-23 21,-32 24,-23" fill="#e74c3c" />
      <polygon points="22,-21 25,-28 28,-21" fill="#c0392b" />
      {/* Wattle */}
      <ellipse cx="24" cy="-4" rx="4" ry="7" fill="#c0392b" />
      {/* Eye */}
      <circle cx="22" cy="-12" r="4" fill="white" />
      <circle cx="23" cy="-12" r="2.5" fill="#1a1a1a" />
      <circle cx="24" cy="-13" r="1" fill="white" />
      {/* Beak */}
      <polygon points="28,-10 36,-8 28,-6" fill="#e8890a" />
      {/* Legs */}
      <line x1="-4" y1="16" x2="-8" y2="28" stroke="#e8890a" strokeWidth="3" />
      <line x1="4" y1="16" x2="6" y2="28" stroke="#e8890a" strokeWidth="3" />
      <line x1="-8" y1="28" x2="-14" y2="30" stroke="#e8890a" strokeWidth="2" />
      <line x1="-8" y1="28" x2="-6" y2="32" stroke="#e8890a" strokeWidth="2" />
      <line x1="6" y1="28" x2="2" y2="32" stroke="#e8890a" strokeWidth="2" />
      <line x1="6" y1="28" x2="10" y2="32" stroke="#e8890a" strokeWidth="2" />
      {/* Poop launching! */}
      {pooping && (
        <ellipse cx="0" cy="22" rx="6" ry="8" fill="#8B6914" opacity="0.9" />
      )}
    </g>
  );
}

function Farmer({ x, facing }) {
  const f = facing === 'right' ? 1 : -1;
  return (
    <g transform={`translate(${x}, ${FARMER_Y}) scale(${f}, 1)`}>
      {/* Overalls */}
      <rect x="-14" y="-20" width="28" height="35" fill="#4a6fa5" rx="4" />
      {/* Shirt */}
      <rect x="-12" y="-20" width="24" height="20" fill="#e8d5a3" rx="3" />
      {/* Head */}
      <circle cx="0" cy="-32" r="16" fill="#f5c5a3" />
      {/* Straw hat */}
      <ellipse cx="0" cy="-46" rx="22" ry="5" fill="#d4a843" />
      <rect x="-10" y="-58" width="20" height="14" fill="#c8981e" rx="3" />
      {/* Eyes */}
      <circle cx="-5" cy="-33" r="2.5" fill="#3d2b1f" />
      <circle cx="5" cy="-33" r="2.5" fill="#3d2b1f" />
      {/* Angry eyebrows */}
      <line x1="-8" y1="-38" x2="-2" y2="-36" stroke="#3d2b1f" strokeWidth="2" />
      <line x1="2" y1="-36" x2="8" y2="-38" stroke="#3d2b1f" strokeWidth="2" />
      {/* Mouth - angry */}
      <path d="M -5,-27 Q 0,-24 5,-27" fill="none" stroke="#3d2b1f" strokeWidth="1.5" />
      {/* Arms */}
      <line x1="14" y1="-15" x2="28" y2="-5" stroke="#f5c5a3" strokeWidth="8" strokeLinecap="round" />
      <line x1="-14" y1="-15" x2="-20" y2="-5" stroke="#f5c5a3" strokeWidth="8" strokeLinecap="round" />
      {/* Legs */}
      <rect x="-12" y="14" width="10" height="20" fill="#3a5a8a" rx="3" />
      <rect x="2" y="14" width="10" height="20" fill="#3a5a8a" rx="3" />
      {/* Boots */}
      <ellipse cx="-7" cy="35" rx="8" ry="5" fill="#3d2b1f" />
      <ellipse cx="7" cy="35" rx="8" ry="5" fill="#3d2b1f" />
    </g>
  );
}

function BabyFarmer({ x }) {
  return (
    <g transform={`translate(${x}, ${FARMER_Y}) scale(0.35)`}>
      <rect x="-14" y="-20" width="28" height="35" fill="#4a6fa5" rx="4" />
      <rect x="-12" y="-20" width="24" height="20" fill="#e8d5a3" rx="3" />
      <circle cx="0" cy="-32" r="16" fill="#f5c5a3" />
      <ellipse cx="0" cy="-46" rx="22" ry="5" fill="#c0392b" />
      <rect x="-10" y="-58" width="20" height="14" fill="#e74c3c" rx="3" />
      <circle cx="-5" cy="-33" r="2.5" fill="#3d2b1f" />
      <circle cx="5" cy="-33" r="2.5" fill="#3d2b1f" />
      <line x1="-8" y1="-38" x2="-2" y2="-36" stroke="#3d2b1f" strokeWidth="2" />
      <line x1="2" y1="-36" x2="8" y2="-38" stroke="#3d2b1f" strokeWidth="2" />
      <path d="M -5,-27 Q 0,-24 5,-27" fill="none" stroke="#3d2b1f" strokeWidth="1.5" />
      <line x1="14" y1="-15" x2="28" y2="-5" stroke="#f5c5a3" strokeWidth="8" strokeLinecap="round" />
      <line x1="-14" y1="-15" x2="-20" y2="-5" stroke="#f5c5a3" strokeWidth="8" strokeLinecap="round" />
      <rect x="-12" y="14" width="10" height="20" fill="#3a5a8a" rx="3" />
      <rect x="2" y="14" width="10" height="20" fill="#3a5a8a" rx="3" />
      <ellipse cx="-7" cy="35" rx="8" ry="5" fill="#3d2b1f" />
      <ellipse cx="7" cy="35" rx="8" ry="5" fill="#3d2b1f" />
    </g>
  );
}

function FarmersWife({ x, facing = 'left' }) {
  const f = facing === 'right' ? 1 : -1;
  return (
    <g transform={`translate(${x}, ${FARMER_Y}) scale(${f}, 1)`}>
      {/* Dress (long pink) */}
      <polygon points="-16,-18 16,-18 22,30 -22,30" fill="#d94a8a" />
      {/* Dress trim */}
      <rect x="-22" y="28" width="44" height="4" fill="#a8316a" />
      {/* Apron (white, narrower) */}
      <polygon points="-10,-14 10,-14 14,26 -14,26" fill="#f5f0e8" />
      <rect x="-10" y="-15" width="20" height="3" fill="#e8d5a3" />
      {/* Apron tie */}
      <rect x="-14" y="-2" width="28" height="3" fill="#c8a843" />
      {/* Head */}
      <circle cx="0" cy="-32" r="15" fill="#f5c5a3" />
      {/* Hair bun behind head */}
      <circle cx="0" cy="-46" r="9" fill="#8b5a2b" />
      {/* Bonnet (white, covers top/sides of head) */}
      <path d="M -16,-36 Q -18,-50 0,-52 Q 18,-50 16,-36 L 14,-32 L -14,-32 Z" fill="#f5f0e8" stroke="#d8c5a8" strokeWidth="1" />
      {/* Bonnet ribbon */}
      <path d="M -10,-30 Q -8,-22 -6,-20" stroke="#d94a8a" strokeWidth="2" fill="none" />
      {/* Eyes */}
      <circle cx="-5" cy="-33" r="2.2" fill="#3d2b1f" />
      <circle cx="5" cy="-33" r="2.2" fill="#3d2b1f" />
      {/* Eyebrows (angry) */}
      <line x1="-8" y1="-38" x2="-2" y2="-36" stroke="#3d2b1f" strokeWidth="2" />
      <line x1="2" y1="-36" x2="8" y2="-38" stroke="#3d2b1f" strokeWidth="2" />
      {/* Mouth - scowl */}
      <path d="M -5,-26 Q 0,-29 5,-26" fill="none" stroke="#3d2b1f" strokeWidth="1.5" />
      {/* Lipstick */}
      <path d="M -4,-27 Q 0,-28 4,-27" fill="none" stroke="#c0392b" strokeWidth="1.2" />
      {/* Arms */}
      <line x1="14" y1="-14" x2="26" y2="-22" stroke="#f5c5a3" strokeWidth="7" strokeLinecap="round" />
      <line x1="-14" y1="-14" x2="-22" y2="-4" stroke="#f5c5a3" strokeWidth="7" strokeLinecap="round" />
      {/* Boots peeking out */}
      <ellipse cx="-8" cy="32" rx="6" ry="3" fill="#3d2b1f" />
      <ellipse cx="8" cy="32" rx="6" ry="3" fill="#3d2b1f" />
    </g>
  );
}

function FryingPan({ x, y, size = 56 }) {
  const half = size / 2;
  return (
    <g transform={`translate(${x}, ${y})`}>
      <g style={{ animation: 'panSpin 0.6s linear infinite', transformBox: 'fill-box', transformOrigin: 'center' }}>
        {/* White outline halo for visibility */}
        <ellipse cx="0" cy="0" rx={half * 0.58} ry={half * 0.53} fill="none" stroke="#ffffff" strokeWidth="1.5" />
        {/* Pan body (dark cast iron) */}
        <ellipse cx="0" cy="0" rx={half * 0.55} ry={half * 0.5} fill="#1a1a1a" stroke="#2a2a2a" strokeWidth="2" />
        {/* Inner ring (cooking surface) */}
        <ellipse cx="0" cy="0" rx={half * 0.45} ry={half * 0.4} fill="#2a2a2a" />
        {/* Inner highlight */}
        <ellipse cx={-half * 0.18} cy={-half * 0.18} rx={half * 0.18} ry={half * 0.12} fill="#3a3a3a" opacity="0.7" />
        {/* Handle (wooden) */}
        <rect x={half * 0.5} y={-half * 0.1} width={half * 0.7} height={half * 0.2} fill="#6b4423" stroke="#3d2b1f" strokeWidth="1" rx="2" />
        {/* Handle grip ridges */}
        <line x1={half * 0.7} y1={-half * 0.1} x2={half * 0.7} y2={half * 0.1} stroke="#3d2b1f" strokeWidth="0.8" />
        <line x1={half * 0.9} y1={-half * 0.1} x2={half * 0.9} y2={half * 0.1} stroke="#3d2b1f" strokeWidth="0.8" />
      </g>
    </g>
  );
}

function Hawk({ x, y, facing = 'right' }) {
  const f = facing === 'right' ? 1 : -1;
  return (
    <g transform={`translate(${x}, ${y}) scale(${f}, 1)`}>
      {/* Tail feathers - spread */}
      <polygon points="-22,0 -34,-8 -30,4" fill="#6b3410" stroke="#ffffff" strokeWidth="1" />
      <polygon points="-22,2 -36,2 -30,8" fill="#8B4513" stroke="#ffffff" strokeWidth="1" />
      <polygon points="-22,4 -34,10 -28,12" fill="#6b3410" stroke="#ffffff" strokeWidth="1" />
      {/* Body */}
      <ellipse cx="0" cy="0" rx="20" ry="14" fill="#8B4513" stroke="#ffffff" strokeWidth="1.5" />
      {/* Chest plumage */}
      <ellipse cx="2" cy="2" rx="14" ry="9" fill="#a0522d" />
      {/* Left wing (rear) - flapping */}
      <g style={{ animation: 'hawkFlap 0.4s ease-in-out infinite alternate', transformBox: 'fill-box', transformOrigin: 'right center' }}>
        <polygon points="-4,-4 -22,-18 -14,-2" fill="#6b3410" stroke="#ffffff" strokeWidth="1.2" />
      </g>
      {/* Right wing (front) - flapping */}
      <g style={{ animation: 'hawkFlap 0.4s ease-in-out infinite alternate-reverse', transformBox: 'fill-box', transformOrigin: 'left center' }}>
        <polygon points="4,-4 22,-18 14,-2" fill="#8B4513" stroke="#ffffff" strokeWidth="1.2" />
      </g>
      {/* Head */}
      <circle cx="16" cy="-6" r="11" fill="#8B4513" stroke="#ffffff" strokeWidth="1.5" />
      {/* Eye */}
      <circle cx="20" cy="-8" r="3.5" fill="#fff" />
      <circle cx="21" cy="-8" r="2.5" fill="#1a1a1a" />
      <circle cx="22" cy="-9" r="0.8" fill="#fff" />
      {/* Beak - sharp hooked */}
      <polygon points="24,-6 32,-7 30,-3 24,-3" fill="#3d2b1f" stroke="#ffffff" strokeWidth="0.8" />
      {/* Brow ridge (angry) */}
      <line x1="13" y1="-13" x2="22" y2="-10" stroke="#3d2b1f" strokeWidth="2" />
    </g>
  );
}

function Coop() {
  return (
    <g transform={`translate(${GAME_W/2 - 110}, ${ROOSTER_Y - 130})`}>
      {/* Main building */}
      <rect x="0" y="60" width="220" height="100" fill="#c8956a" rx="3" />
      {/* Siding */}
      <line x1="0" y1="75" x2="220" y2="75" stroke="#b07850" strokeWidth="1.5" />
      <line x1="0" y1="92" x2="220" y2="92" stroke="#b07850" strokeWidth="1.5" />
      <line x1="0" y1="109" x2="220" y2="109" stroke="#b07850" strokeWidth="1.5" />
      <line x1="0" y1="126" x2="220" y2="126" stroke="#b07850" strokeWidth="1.5" />
      <line x1="0" y1="143" x2="220" y2="143" stroke="#b07850" strokeWidth="1.5" />
      {/* Roof - angular */}
      <polygon points="-5,62 110,10 225,55 225,62 -5,62" fill="#7a1a0a" />
      <polygon points="110,10 150,22 225,55 150,45" fill="#8B1a0a" />
      {/* Chimney */}
      <rect x="40" y="0" width="18" height="30" fill="#5a3020" />
      <rect x="37" y="-4" width="24" height="7" fill="#3a2010" />
      {/* Window */}
      <rect x="15" y="72" width="30" height="24" fill="#87ceeb" stroke="#5a3020" strokeWidth="2" rx="2" />
      <line x1="30" y1="72" x2="30" y2="96" stroke="#5a3020" strokeWidth="1" />
      <line x1="15" y1="84" x2="45" y2="84" stroke="#5a3020" strokeWidth="1" />
      {/* Door */}
      <rect x="90" y="110" width="40" height="50" fill="#2a1000" rx="3" />
      <circle cx="125" cy="135" r="3" fill="#F0B800" />
      {/* Sign */}
      <rect x="20" y="56" width="180" height="16" fill="#F0B800" rx="3" />
      <text x="110" y="68" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#5a3e00" fontFamily="sans-serif">CHICKEN COOP</text>
    </g>
  );
}

function Poop({ x, y }) {
  return (
    <g transform={`translate(${x}, ${y})`}>
      <ellipse cx="0" cy="0" rx="7" ry="10" fill="#6B4E10" />
      <ellipse cx="0" cy="-6" rx="5" ry="6" fill="#7a5a18" />
      <ellipse cx="0" cy="-10" rx="3" ry="4" fill="#8B6420" />
      <ellipse cx="1" cy="-3" rx="2" ry="1.5" fill="#9a7428" opacity="0.6" />
    </g>
  );
}

function Weapon({ x, y, type, rotation, size = 60 }) {
  const half = size / 2;
  return (
    <image
      href={`/images/weapons/${type}.png`}
      x={x - half}
      y={y - half}
      width={size}
      height={size}
      transform={`rotate(${rotation}, ${x}, ${y})`}
    />
  );
}

function Explosion({ x, y }) {
  return (
    <g transform={`translate(${x}, ${y})`}>
      <circle cx="0" cy="0" r="20" fill="#F0B800" opacity="0.7" />
      <circle cx="0" cy="0" r="12" fill="#ff6b00" opacity="0.8" />
      <polygon points="0,-25 5,-10 20,-15 8,-3 22,5 8,5 12,20 0,8 -12,20 -8,5 -22,5 -8,-3 -20,-15 -5,-10" fill="#FFD700" opacity="0.9" />
    </g>
  );
}

function ScoreBreakdown({ score, accentColor }) {
  const acc = score.accuracy != null ? Math.round(score.accuracy) : 0;
  const mult = score.multiplier ?? 1.0;
  const rows = [
    { label: `Rounds Completed (${score.rounds ?? 0})`, value: score.roundPoints ?? 0, color: '#5AAAE0' },
    { label: `Weapons Hit (${score.hits ?? 0} × 1000)`, value: score.weaponPoints ?? 0, color: '#4CAF50' },
    { label: `Baby Farmers Hit (${score.babyFarmersHit ?? 0} × 500)`, value: score.babyPoints ?? 0, color: '#F0B800' },
    ...(score.foxEncountered ? [{ label: '🦊 Fox Bonus!', value: score.foxBonus ?? 0, color: '#FF6B35' }] : []),
    ...(score.virusEncountered ? [{ label: (score.virusBonus ?? 0) >= 0 ? '🦠 Virus Bonus!' : '🦠 Virus Penalty!', value: score.virusBonus ?? 0, color: (score.virusBonus ?? 0) >= 0 ? '#00C853' : '#E05A5A' }] : []),
    ...(score.fryingPanEncountered ? [{ label: (score.fryingPanBonus ?? 0) >= 0 ? '🍳 Frying Pan Bonus!' : '🍳 Frying Pan Penalty!', value: score.fryingPanBonus ?? 0, color: (score.fryingPanBonus ?? 0) >= 0 ? '#FF8FB1' : '#E05A5A' }] : []),
    ...(score.hawkEncountered ? [{ label: (score.hawkBonus ?? 0) >= 0 ? '🦅 Super Hawk Bonus!' : '🦅 Super Hawk Penalty!', value: score.hawkBonus ?? 0, color: (score.hawkBonus ?? 0) >= 0 ? '#FFA500' : '#E05A5A' }] : []),
    { label: 'Complete All 10 Rounds Bonus', value: score.fullGameBonus ?? 0, color: '#9B59B6' },
  ];
  return (
    <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 12 }}>
      {rows.map((r, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, gap: 24 }}>
          <span style={{ color: '#888' }}>{r.label}</span>
          <span style={{ color: r.color, fontWeight: 700 }}>{r.value.toLocaleString()}</span>
        </div>
      ))}
      <div style={{ borderTop: '1px solid #333', margin: '10px 0', paddingTop: 10, display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ color: '#888' }}>Accuracy: {acc}% → ×{mult.toFixed(1)}</span>
        <span style={{ color: '#888' }}>multiplier</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 18, fontWeight: 900, marginTop: 4 }}>
        <span style={{ color: '#888', fontSize: 13 }}>FINAL SCORE</span>
        <span style={{ color: accentColor }}>{(score.finalScore ?? 0).toLocaleString()}</span>
      </div>
    </div>
  );
}

function LeaderboardDisplay({ leaderboard, currentWallet, currentScore, title = '🏆 TOP 100 — CHICKEN DROPPINGS LEADERBOARD' }) {
  const shortW = (w) => w ? `${w.slice(0,4)}...${w.slice(-4)}` : '????';
  const currentEntry = leaderboard.find(e => e.wallet === shortW(currentWallet) || e.fullWallet === currentWallet);
  const currentRank = currentEntry ? leaderboard.indexOf(currentEntry) + 1 : null;
  return (
    <div style={{ width: '100%', maxWidth: 500, background: '#111', border: '1px solid #2a2a2a', borderRadius: 12, padding: 20, fontFamily: "'DM Mono', monospace" }}>
      <div style={{ fontSize: 12, color: '#F0B800', letterSpacing: 2, marginBottom: 12, textAlign: 'center' }}>
        {title}
      </div>
      {currentWallet && (
        <div style={{ background: 'rgba(240,184,0,0.15)', border: '1px solid #F0B800', borderRadius: 6, padding: '6px 10px', marginBottom: 8, fontSize: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <span style={{ color: '#F0B800', minWidth: 120 }}>YOUR POSITION IS: #{currentRank || '—'}</span>
            <span style={{ color: '#aaa', flex: 1, textAlign: 'center' }}>{shortW(currentWallet)}</span>
            <span style={{ color: '#4CAF50', minWidth: 80, textAlign: 'right' }}>{currentScore > 0 ? currentScore.toLocaleString() + ' pts' : ''}</span>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', fontSize: 10, color: '#666', letterSpacing: 1, borderBottom: '1px solid #2a2a2a', marginBottom: 4, fontFamily: "'DM Mono', monospace" }}>
        <span style={{ minWidth: 28 }}>#</span>
        <span style={{ flex: 1 }}>WALLET</span>
        <span style={{ marginRight: 8 }}>ROUND</span>
        <span style={{ marginRight: 8, minWidth: 40, textAlign: 'right' }}>ACC%</span>
        <span>SCORE</span>
      </div>
      <div style={{ maxHeight: 300, overflowY: 'scroll', scrollbarWidth: 'thin', scrollbarColor: '#F0B800 #1a1a1a' }}>
        {(() => {
          const lb = leaderboard;
          const rows = [];
          for (let i = 0; i < 100; i++) {
            const entry = lb[i];
            const isMe = entry && currentWallet && (entry.wallet === shortW(currentWallet) || entry.fullWallet === currentWallet);
            rows.push(
              <div key={i} style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '4px 8px',
                background: isMe ? 'rgba(240,184,0,0.1)' : i % 2 === 0 ? '#111' : '#0a0a0a',
                borderRadius: 4,
                fontSize: 11,
                border: isMe ? '1px solid rgba(240,184,0,0.3)' : 'none',
                fontFamily: "'DM Mono', monospace",
              }}>
                <span style={{ color: i < 3 ? '#F0B800' : '#555', minWidth: 28 }}>#{i + 1}</span>
                {entry ? (
                  <>
                    <span style={{ color: isMe ? '#F0B800' : '#aaa', flex: 1 }}>{entry.wallet}</span>
                    <span style={{ color: '#888', fontSize: 10, marginRight: 8 }}>Rd {entry.rounds || '?'}</span>
                    <span style={{ color: '#888', fontSize: 10, marginRight: 8, minWidth: 40, textAlign: 'right' }}>{Math.round(entry.accuracy || 0)}%</span>
                    <span style={{ color: i < 3 ? '#F0B800' : '#bbb' }}>{(entry.score || 0).toLocaleString()}</span>
                  </>
                ) : (
                  <span style={{ color: '#333', flex: 1, fontStyle: 'italic' }}>no score yet</span>
                )}
              </div>
            );
          }
          return rows;
        })()}
      </div>
    </div>
  );
}

export default function ChickenDroppings() {
  const { publicKey, sendTransaction } = useWallet();
  const { isRegistered, isChecking, isRegistering, registrationError, registerWallet } = useWalletRegistration();
  const prevIsRegisteredRef = useRef(null);
  useEffect(() => {
    if (isRegistered === true && prevIsRegisteredRef.current === null) {
      setShowWelcomeBack(true);
      const timer = setTimeout(() => setShowWelcomeBack(false), 3000);
      prevIsRegisteredRef.current = true;
      return () => clearTimeout(timer);
    }
    if (isRegistered !== null) {
      prevIsRegisteredRef.current = isRegistered;
    }
  }, [isRegistered]);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const res = await fetch(ORACLE_URL + '/health');
        if (cancelled) return;
        if (res.ok) {
          const data = await res.json();
          setOracleStatus(data && data.status === 'ok' ? 'online' : 'offline');
        } else {
          setOracleStatus('offline');
        }
      } catch (e) {
        if (!cancelled) setOracleStatus('offline');
      }
    };
    check();
    const id = setInterval(check, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(ORACLE_URL + '/leaderboard');
        if (cancelled) return;
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data)) setLeaderboard(data);
        }
      } catch (e) { /* leaderboard stays empty */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const { connection } = useConnection();
  const navigate = useNavigate();
  const isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const canvasRef = useRef(null);
  const leftBtnRef = useRef(null);
  const rightBtnRef = useRef(null);
  const shootBtnRef = useRef(null);
  const gameRef = useRef({
    roosterX: GAME_W / 2,
    poops: [],
    weapons: [],
    explosions: [],
    babyFarmers: [],
    bonusFlashes: [],
    keys: {},
    spaceWasUp: true,
    shots: 0,
    hits: 0,
    babyFarmersHit: 0,
    weaponTimer: null,
    roundTimer: null,
    roundSeconds: 0,
    farmerX: GAME_W / 2,
    farmerDir: 1,
    farmerSpeed: 2,
    farmerTarget: GAME_W / 2,
    farmerChangeTimer: 0,
    weaponsThrown: 0,
    weaponsThrownSinceBaby: 0,
    weaponsRequired: 0,
    showStartOverlay: false,
    superWeapons: [],
    roundWinTriggered: false,
    currentRoundIdx: 0,
    babyFarmersSpawnedThisRound: false,
    fox: null,
    foxSpawnMs: 0,
    foxSpawned: false,
    foxBonusTotal: 0,
    viruses: [],
    virusesSpawned: 0,
    virusBonusTotal: 0,
    virusWarningShown: false,
    virusSpawnMs: 0,
    farmersWife: null,
    fryingPans: [],
    fryingPanBonusTotal: 0,
    wifeSpawned: false,
    wifeSpawnMs: 0,
    wifeWarningShown: false,
    hawks: [],
    hawkBonusTotal: 0,
    hawksSpawned: false,
    hawkSpawnMs: 0,
    hawkHitsThisRound: 0,
    bothHawksHitThisRound: false,
    hawkWarningShown: false,
    chickenCount: 1,
    paused: false,
  });

  const [gameState, setGameState] = useState('intro'); // intro, playing, dead, roundwin, gameover, won
  const [currentRound, setCurrentRound] = useState(0);
  const [score, setScore] = useState({ rounds: 0, timeAlive: 0, shots: 0, hits: 0 });
  const [roundTime, setRoundTime] = useState(0);
  const [roosterX, setRoosterX] = useState(GAME_W / 2);
  const [poops, setPoops] = useState([]);
  const [weapons, setWeapons] = useState([]);
  const [explosions, setExplosions] = useState([]);
  const [farmerX, setFarmerX] = useState(GAME_W / 2);
  const [farmerFacing, setFarmerFacing] = useState('right');
  const [pooping, setPooping] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [oracleStatus, setOracleStatus] = useState('checking');
  const gameTokenRef = useRef(null);
  const [paid, setPaid] = useState(false);
  const [autoRedirectTimer, setAutoRedirectTimer] = useState(60);
  const [bonanzaPool, setBonanzaPool] = useState(0);
  const [chickenDropPool, setChickenDropPool] = useState(0);
  const [isTryMode, setIsTryMode] = useState(false);
  const [tryRedirectTimer, setTryRedirectTimer] = useState(20);
  const [roundwinTimer, setRoundwinTimer] = useState(4);
  const [showStartOverlay, setShowStartOverlay] = useState(false);
  const [startCountdown, setStartCountdown] = useState(3);
  const [gameReady, setGameReady] = useState(false);
  const [babyFarmers, setBabyFarmers] = useState([]);
  const [bonusFlashes, setBonusFlashes] = useState([]);
  const [showBabyBonus, setShowBabyBonus] = useState(false);
  const [liveScore, setLiveScore] = useState(0);
  const [superWeapons, setSuperWeapons] = useState([]);
  const [fox, setFox] = useState(null);
  const [foxBonusText, setFoxBonusText] = useState(null);
  const [viruses, setViruses] = useState([]);
  const [virusWarning, setVirusWarning] = useState(false);
  const [virusBonusText, setVirusBonusText] = useState(null);
  const [virusEscapeText, setVirusEscapeText] = useState(null);
  const [farmersWife, setFarmersWife] = useState(null);
  const [fryingPans, setFryingPans] = useState([]);
  const [fryingPanBonusText, setFryingPanBonusText] = useState(null);
  const [fryingPanEscapeText, setFryingPanEscapeText] = useState(null);
  const [wifeWarning, setWifeWarning] = useState(false);
  const [hawks, setHawks] = useState([]);
  const [hawkBonusText, setHawkBonusText] = useState(null);
  const [hawkEscapeText, setHawkEscapeText] = useState(null);
  const [hawkWarning, setHawkWarning] = useState(false);
  const [chickenCount, setChickenCount] = useState(1);
  const [chickenMessage, setChickenMessage] = useState(null);
  const [roundStartMessage, setRoundStartMessage] = useState(false);
  const [showHumanCheck, setShowHumanCheck] = useState(false);
  const [humanCheckTimer, setHumanCheckTimer] = useState(5);
  const [humanCheckResult, setHumanCheckResult] = useState(null); // null | 'caught' | 'missed'
  const [pendingEntry, setPendingEntry] = useState(null);
  const [bouncePos, setBouncePos] = useState({ x: 130, y: 80 });
  const [verifyAttempts, setVerifyAttempts] = useState(0);
  const [clicksUsed, setClicksUsed] = useState(0);
  const [selectedGameType, setSelectedGameType] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactMessage, setContactMessage] = useState('');
  const [contactStatus, setContactStatus] = useState(null);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showRegisterConfirm, setShowRegisterConfirm] = useState(false);
  const [showWelcomeBack, setShowWelcomeBack] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // null | 'pending' | 'confirmed' | 'error'
  const [paymentError, setPaymentError] = useState(null);
  const effectivePublicKey = publicKey?.toString() || null;

  useEffect(() => {
    if (!effectivePublicKey) return;
    let cancelled = false;
    (async () => {
      const gameIds = ['mini', 'standard', 'mega'];
      try {
        const responses = await Promise.all(
          gameIds.map(id => fetch(`${ORACLE_URL}/status/${id}`).then(r => r.ok ? r.json() : null).catch(() => null))
        );
        if (cancelled) return;
        let pendingPick = null;
        let autoAssigned = null;
        for (let i = 0; i < gameIds.length; i++) {
          const data = responses[i];
          if (!data) continue;
          const gameId = gameIds[i];
          const entryWallets = data.entryWallets || [];
          const squares = data.squares || {};
          if (entryWallets.includes(effectivePublicKey)) {
            if (!pendingPick) pendingPick = { gameId };
            continue;
          }
          let ownedSquareNum = null;
          let ownedType = null;
          for (const [sqNum, value] of Object.entries(squares)) {
            const wallet = typeof value === 'string' ? value : value?.wallet;
            if (wallet === effectivePublicKey) {
              ownedSquareNum = Number(sqNum);
              ownedType = typeof value === 'string' ? 'delegate' : (value?.type || 'delegate');
              break;
            }
          }
          if (ownedType === 'free') {
            if (!autoAssigned) autoAssigned = { gameId, squareNum: ownedSquareNum };
          }
          // not in entryWallets and not 'free' → do nothing
        }
        if (cancelled) return;
        if (pendingPick) {
          const cfg = GAME_CONFIGS[pendingPick.gameId];
          if (cfg) navigate(cfg.route, { state: { pendingPick: true, gameId: pendingPick.gameId } });
        } else if (autoAssigned) {
          const cfg = GAME_CONFIGS[autoAssigned.gameId];
          if (cfg) navigate(cfg.route, { state: { autoAssigned: true, squareNum: autoAssigned.squareNum, gameId: autoAssigned.gameId } });
        }
      } catch (e) { /* silent — landing page just doesn't auto-redirect */ }
    })();
    return () => { cancelled = true; };
  }, [effectivePublicKey, navigate]);

  const musicRef = useRef(null);
  const bounceRef = useRef({ x: 130, y: 80, vx: 4.5, vy: 3.2 });
  const humanCheckLoopRef = useRef(null);
  const humanCheckCountdownRef = useRef(null);
  const pendingEntryRef = useRef(null);
  const verifyAttemptsRef = useRef(0);
  const clicksUsedRef = useRef(0);
  const paidRef = useRef(paid);
  const isTryModeRef = useRef(isTryMode);

  useEffect(() => { paidRef.current = paid; }, [paid]);
  useEffect(() => { isTryModeRef.current = isTryMode; }, [isTryMode]);

  const startMusic = () => {
    console.log('startMusic called');
    if (!musicRef.current) {
      console.log('creating new Audio element');
      musicRef.current = new Audio('/droppings-music.mp3');
      musicRef.current.loop = true;
      musicRef.current.volume = 0;
    }
    console.log('audio paused?', musicRef.current.paused);
    console.log('audio src:', musicRef.current.src);
    if (!musicRef.current.paused) {
      console.log('already playing, returning');
      return;
    }
    musicRef.current.play().then(() => {
      console.log('play() succeeded');
    }).catch((err) => {
      console.log('play() failed:', err);
    });
    let vol = 0;
    const fade = setInterval(() => {
      if (!musicRef.current) { clearInterval(fade); return; }
      vol = Math.min(0.5, vol + 0.02);
      musicRef.current.volume = vol;
      if (vol >= 0.5) clearInterval(fade);
    }, 100);
  };

  const stopMusic = () => {
    if (!musicRef.current) return;
    let vol = musicRef.current.volume;
    const fade = setInterval(() => {
      vol = Math.max(0, vol - 0.02);
      musicRef.current.volume = vol;
      if (vol <= 0) { clearInterval(fade); musicRef.current.pause(); }
    }, 100);
  };

  const roundConfig = ROUNDS[currentRound] || ROUNDS[9];

  const calcScore = useCallback((roundsCompleted, shots, hits, babyFarmersHit, foxBonusTotal = 0, virusBonusTotal = 0, fryingPanBonusTotal = 0, hawkBonusTotal = 0) => {
    let roundPoints = 0;
    for (let i = 0; i < Math.min(roundsCompleted, 10); i++) roundPoints += ROUND_POINTS[i];
    const weaponPoints = hits * 1000;
    const babyPoints = babyFarmersHit * 500;
    const foxBonus = foxBonusTotal || 0;
    const virusBonus = virusBonusTotal || 0;
    const fryingPanBonus = fryingPanBonusTotal || 0;
    const hawkBonus = hawkBonusTotal || 0;
    const fullGameBonus = roundsCompleted >= 10 ? 200000 : 0;
    const accuracy = shots > 0 ? (hits / shots) * 100 : 0;
    const multiplier =
      accuracy >= 80 ? 2.5 :
      accuracy >= 75 ? 2.4 :
      accuracy >= 70 ? 2.3 :
      accuracy >= 65 ? 2.2 :
      accuracy >= 60 ? 2.1 :
      accuracy >= 55 ? 2.0 :
      accuracy >= 50 ? 1.9 :
      accuracy >= 45 ? 1.8 :
      accuracy >= 40 ? 1.7 :
      accuracy >= 35 ? 1.6 :
      accuracy >= 30 ? 1.5 :
      accuracy >= 25 ? 1.4 :
      accuracy >= 20 ? 1.3 :
      accuracy >= 15 ? 1.2 :
      accuracy >= 10 ? 1.1 : 1.0;
    const baseScore = roundPoints + weaponPoints + babyPoints + foxBonus + virusBonus + fryingPanBonus + hawkBonus + fullGameBonus;
    return { roundPoints, weaponPoints, babyPoints, foxBonus, virusBonus, fryingPanBonus, hawkBonus, fullGameBonus, accuracy, multiplier, finalScore: Math.round(baseScore * multiplier) };
  }, []);

  const submitScoreToOracle = useCallback(async ({ shortWallet, walletAddr, finalScore, rounds, accuracy }) => {
    if (!gameTokenRef.current) return;
    const token = gameTokenRef.current;
    gameTokenRef.current = null;
    try {
      const res = await fetch(ORACLE_URL + '/leaderboard/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          walletAddress: walletAddr,
          score: finalScore,
          rounds,
          accuracy,
          shortWallet,
        }),
      });
      if (!res.ok) return;
      const refreshed = await fetch(ORACLE_URL + '/leaderboard');
      if (!refreshed.ok) return;
      const data = await refreshed.json();
      if (Array.isArray(data)) setLeaderboard(data);
    } catch (e) { /* silent */ }
  }, [setLeaderboard]);

  const endGame = useCallback((won) => {
    const g = gameRef.current;
    clearInterval(g.weaponTimer);
    clearInterval(g.roundTimer);
    stopMusic();
    const breakdown = calcScore(currentRound, g.shots, g.hits, g.babyFarmersHit, g.foxBonusTotal, g.virusBonusTotal, g.fryingPanBonusTotal, g.hawkBonusTotal);
    const { roundPoints, weaponPoints, babyPoints, foxBonus, virusBonus, fryingPanBonus, hawkBonus, fullGameBonus, accuracy, multiplier, finalScore } = breakdown;
    setScore({ rounds: currentRound, timeAlive: g.roundSeconds, shots: g.shots, hits: g.hits, accuracy, finalScore, babyFarmersHit: g.babyFarmersHit, roundPoints, weaponPoints, babyPoints, foxBonus, virusBonus, fryingPanBonus, hawkBonus, fullGameBonus, multiplier, foxEncountered: g.fox !== null || g.foxBonusTotal !== 0, virusEncountered: g.virusesSpawned > 0, fryingPanEncountered: g.wifeSpawned, hawkEncountered: g.hawksSpawned || g.hawkBonusTotal !== 0 });

    const walletAddr = effectivePublicKey || 'TEST...TEST';
    const shortWallet = walletAddr.slice(0, 4) + '...' + walletAddr.slice(-4);

    if (!isTryModeRef.current && paidRef.current) {
      // Check if score qualifies for top 100 → show human verification before submitting
      if (effectivePublicKey && gameTokenRef.current) {
        const existing = leaderboard || [];
        const WALLET_MAX_ENTRIES = 5;
        const walletEntries = existing.filter(e => e.wallet === shortWallet || e.fullWallet === walletAddr).sort((a, b) => b.score - a.score);
        const walletFull = walletEntries.length >= WALLET_MAX_ENTRIES;
        const beatsWalletLowest = !walletFull || finalScore > (walletEntries[walletEntries.length - 1]?.score ?? 0);
        const qualifies = beatsWalletLowest && (existing.length < 100 || finalScore > (existing[existing.length - 1]?.score ?? 0));
        if (qualifies) {
          const nextState = won ? 'won' : currentRound >= 10 ? 'won' : 'dead';
          const entry = { shortWallet, walletAddr, finalScore, rounds: currentRound, accuracy, nextState };
          pendingEntryRef.current = entry;
          setPendingEntry(entry);
          verifyAttemptsRef.current = 0;
          setVerifyAttempts(0);
          setShowHumanCheck(true);
          return; // setGameState called after human check resolves
        }
      }
    }

    setGameState((isTryModeRef.current || !paidRef.current) ? 'tryComplete' : (won ? 'won' : currentRound >= 10 ? 'won' : 'dead'));
  }, [currentRound, effectivePublicKey, calcScore, paid, isTryMode, selectedGameType]); // eslint-disable-line

  const catchChicken = useCallback(() => {
    if (humanCheckResult !== null) return;
    clearInterval(humanCheckLoopRef.current);
    clearInterval(humanCheckCountdownRef.current);
    setHumanCheckResult('caught');
    const entry = pendingEntryRef.current;
    if (entry) submitScoreToOracle(entry);
    setTimeout(() => {
      const nextState = pendingEntryRef.current?.nextState || 'dead';
      setShowHumanCheck(false);
      setGameState(nextState);
      pendingEntryRef.current = null;
      setPendingEntry(null);
      setHumanCheckResult(null);
      verifyAttemptsRef.current = 0;
      setVerifyAttempts(0);
    }, 4500);
  }, [humanCheckResult, submitScoreToOracle]);

  const startRound = useCallback((roundIdx) => {
    const g = gameRef.current;
    const config = ROUNDS[roundIdx];
    clearInterval(g.weaponTimer);
    clearInterval(g.roundTimer);
    g.keys = {};
    g.poops = [];
    g.weapons = [];
    g.explosions = [];
    g.babyFarmers = [];
    g.bonusFlashes = [];
    g.roundSeconds = 0;
    g.weaponsThrown = 0;
    g.weaponsThrownSinceBaby = 0;
    g.weaponsRequired = config.count;
    g.farmerX = GAME_W / 2;
    g.farmerTarget = GAME_W / 2;
    g.farmerSpeed = 2;
    g.farmerChangeTimer = 0;
    g.superWeapons = [];
    g.roundWinTriggered = false;
    g.currentRoundIdx = roundIdx;
    g.babyFarmersSpawnedThisRound = false;
    g.fox = null;
    g.foxSpawnMs = 5000 + Math.random() * 10000;
    g.foxSpawned = false;
    g.viruses = [];
    g.virusesSpawned = 0;
    g.virusWarningShown = false;
    g.virusSpawnMs = 8000 + Math.random() * 7000;
    g.farmersWife = null;
    g.fryingPans = [];
    g.wifeSpawned = false;
    g.wifeWarningShown = false;
    g.wifeSpawnMs = 8000;
    g.hawks = [];
    g.hawksSpawned = false;
    g.hawkSpawnMs = 2000 + Math.random() * 2000;
    g.hawkHitsThisRound = 0;
    g.bothHawksHitThisRound = false;
    g.hawkWarningShown = false;

    setPoops([]);
    setWeapons([]);
    setExplosions([]);
    setBabyFarmers([]);
    setBonusFlashes([]);
    setSuperWeapons([]);
    setFox(null);
    setFoxBonusText(null);
    setViruses([]);
    setVirusWarning(false);
    setVirusBonusText(null);
    setVirusEscapeText(null);
    setFarmersWife(null);
    setFryingPans([]);
    setFryingPanBonusText(null);
    setFryingPanEscapeText(null);
    setWifeWarning(false);
    setHawks([]);
    setHawkBonusText(null);
    setHawkEscapeText(null);
    setHawkWarning(false);
    setRoundTime(0);
    setCurrentRound(roundIdx);
    setGameState('playing');
    if (roundIdx === 0) {
      setRoundStartMessage(true);
      setTimeout(() => {
        setRoundStartMessage(false);
        gameRef.current.showStartOverlay = true;
        setShowStartOverlay(true);
        startMusic();
      }, 3000);
    } else {
      setGameReady(true);
    }

    g.roundTimer = setInterval(() => {
      if (gameRef.current.paused) return;
      if (g.showStartOverlay) return;
      g.roundSeconds++;
      setRoundTime(g.roundSeconds);
    }, 1000);

    g.weaponTimer = setInterval(() => {
      if (gameRef.current.paused) return;
      if (g.showStartOverlay) return;
      if (g.weaponsThrown >= config.count) {
        clearInterval(g.weaponTimer);
        return; // Round end is handled by game loop via super weapon logic
      }
      g.weaponsThrown++;
      g.weaponsThrownSinceBaby++;

      // Spawn super weapon every 4 throws
      if (g.weaponsThrown > 0 && g.weaponsThrown % 4 === 0) {
        const normalSize = roundIdx <= 5 ? 72 : 66;
        const superSize = config.weapon === 'shovel' ? Math.round(normalSize * 1.15) : normalSize;
        const baseSpeed = 2.5;
        const roundSpeedMultiplier = 1 + (roundIdx / 9);
        const typeMultiplier = (config.weapon === 'sprayer' || config.weapon === 'mean_dog') ? 1.0 : 1.2;
        const speed = baseSpeed * roundSpeedMultiplier * typeMultiplier;
        g.superWeapons.push({
          id: Date.now() + Math.random(),
          x: 50 + Math.random() * (GAME_W - 100),
          y: FARMER_Y,
          speed,
          type: config.weapon,
          size: superSize,
          hit: false,
        });
      }

      if (g.weaponsThrownSinceBaby >= 4 && !g.babyFarmersSpawnedThisRound) {
        g.babyFarmersSpawnedThisRound = true;
        g.weaponsThrownSinceBaby = 0;
        const newBabies = Array.from({ length: 6 }, () => ({
          id: Date.now() + Math.random(),
          x: g.farmerX,
          y: FARMER_Y,
          vx: (Math.random() > 0.5 ? 1 : -1) * (2 + Math.random() * 2),
          alive: true,
        }));
        g.babyFarmers = [...g.babyFarmers, ...newBabies];
        setShowBabyBonus(true);
        setTimeout(() => setShowBabyBonus(false), 3000);
      }
      const baseWeaponSize = roundIdx <= 5 ? 72 : 66;
      const weaponSize = config.weapon === 'shovel' ? Math.round(baseWeaponSize * 1.15) : baseWeaponSize;
      const weapon = {
        id: Date.now() + Math.random(),
        x: g.farmerX,
        y: FARMER_Y - 50,
        vx: (Math.random() - 0.5) * 2,
        vy: -config.speed * (4 + Math.random()) * 0.75,
        type: config.weapon,
        rotation: 0,
        rotSpeed: (Math.random() - 0.5) * 15,
        active: true,
        size: weaponSize,
      };
      g.weapons.push(weapon);
    }, config.interval);
  }, [endGame]);

  const startGame = useCallback(async () => {
    const g = gameRef.current;
    g.shots = 0;
    g.hits = 0;
    g.babyFarmersHit = 0;
    g.foxBonusTotal = 0;
    g.virusBonusTotal = 0;
    g.fryingPanBonusTotal = 0;
    g.hawkBonusTotal = 0;
    g.chickenCount = 1;
    setChickenCount(1);
    g.paused = false;
    g.gameOverTriggered = false;
    g.roosterX = GAME_W / 2;
    setRoosterX(GAME_W / 2);
    setGameReady(false);
    gameTokenRef.current = null;
    if (effectivePublicKey && oracleStatus === 'online') {
      try {
        const res = await fetch(ORACLE_URL + '/leaderboard/start', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ walletAddress: effectivePublicKey }),
        });
        if (!res.ok) throw new Error('start failed');
        const data = await res.json();
        gameTokenRef.current = data.token;
      } catch (e) {
        setOracleStatus('offline');
        return;
      }
    }
    startRound(0);
  }, [startRound, effectivePublicKey, oracleStatus]);

  // Main game loop
  useEffect(() => {
    if (gameState !== 'playing') return;
    if (showStartOverlay) return;
    if (!gameReady) return;

    const loop = setInterval(() => {
      const g = gameRef.current;
      if (g.paused) return;
      if (g.gameOverTriggered) return;

      // Move rooster
      if (g.keys['ArrowLeft']) g.roosterX = Math.max(30, g.roosterX - ROOSTER_SPEED);
      if (g.keys['ArrowRight']) g.roosterX = Math.min(GAME_W - 30, g.roosterX + ROOSTER_SPEED);

      // Fire poop
      if (g.keys[' '] && g.spaceWasUp) {
        g.spaceWasUp = false;
        g.shots++;
        if (g.chickenCount >= 2) {
          g.poops.push({ id: Date.now() + Math.random(), x: g.roosterX - 20, y: ROOSTER_Y + 30 });
          g.poops.push({ id: Date.now() + Math.random() + 0.1, x: g.roosterX + 20, y: ROOSTER_Y + 30 });
        } else {
          g.poops.push({ id: Date.now() + Math.random(), x: g.roosterX, y: ROOSTER_Y + 30 });
        }
        setPooping(true);
        setTimeout(() => setPooping(false), 150);
      }
      if (!g.keys[' ']) g.spaceWasUp = true;

      // Move farmer (erratic targeting)
      g.farmerChangeTimer--;
      if (g.farmerChangeTimer <= 0) {
        g.farmerTarget = 50 + Math.random() * (GAME_W - 100);
        g.farmerSpeed = 1.5 + Math.random() * 2.5;
        g.farmerChangeTimer = 30 + Math.floor(Math.random() * 60);
      }
      const fdx = g.farmerTarget - g.farmerX;
      const farmerDir = fdx > 0 ? 1 : -1;
      g.farmerX = Math.max(50, Math.min(GAME_W - 50, g.farmerX + farmerDir * g.farmerSpeed));
      setFarmerX(g.farmerX);
      setFarmerFacing(farmerDir > 0 ? 'right' : 'left');

      // Move baby farmers
      g.babyFarmers = g.babyFarmers
        .filter(b => b.alive)
        .map(b => {
          let vx = b.vx;
          let nx = b.x + vx;
          if (nx < 20 || nx > GAME_W - 20) { vx *= -1; nx = b.x + vx; }
          return { ...b, x: nx, vx };
        });

      // Move poops down
      g.poops = g.poops
        .map(p => ({ ...p, y: p.y + POOP_SPEED }))
        .filter(p => p.y < GAME_H);

      // Move weapons
      g.weapons = g.weapons.map(w => {
        if (!w.active) return w;
        let ny = w.y + w.vy;
        let nx = w.x + w.vx;
        w.vy += 0.15; // gravity
        w.rotation += w.rotSpeed;
        if (ny < 0) { w.vy *= -0.7; ny = 0; }
        if (nx < 20 || nx > GAME_W - 20) { w.vx *= -1; }
        return { ...w, x: nx, y: ny };
      });

      // Move super weapons upward
      g.superWeapons = g.superWeapons.map(s => s.hit ? s : { ...s, y: s.y - s.speed });

      // Fox spawn countdown
      if (!g.foxSpawned) {
        g.foxSpawnMs -= 16;
        if (g.foxSpawnMs <= 0) {
          g.foxSpawned = true;
          const baseY = GAME_H / 2;
          g.fox = { x: -50, baseY, y: baseY, frame: 0, speed: 2.1 + (g.currentRoundIdx / 9) * 5.1 };
        }
      }

      // Fox movement and exit
      if (g.fox) {
        g.fox.x += g.fox.speed;
        g.fox.frame++;
        g.fox.y = g.fox.baseY + Math.sin(g.fox.frame * 0.05) * 40;
        if (g.fox.x > GAME_W + 50) {
          g.fox = null;
        }
      }

      // Virus spawn (5 per round, spaced 8-15s apart)
      if (g.virusesSpawned < 5) {
        g.virusSpawnMs -= 16;
        if (g.virusSpawnMs <= 0) {
          const roundIdx = g.currentRoundIdx;
          const virusSpeed = 1.5 * (1 + (roundIdx / 9) * 4);
          g.viruses.push({
            id: Date.now() + Math.random(),
            x: 40 + Math.random() * (GAME_W - 80),
            y: GAME_H,
            speed: virusSpeed,
            frame: Math.floor(Math.random() * 100),
          });
          g.virusesSpawned++;
          g.virusSpawnMs = 8000 + Math.random() * 7000;
          if (!g.virusWarningShown) {
            g.virusWarningShown = true;
            // Delay virus warning until after baby farmer banner disappears
            // Baby farmer banner shows for 3 seconds - delay by that amount
            const babyBannerDuration = 3000; // match the baby farmer banner duration
            setTimeout(() => {
              setVirusWarning(true);
              setTimeout(() => setVirusWarning(false), 3000);
            }, babyBannerDuration);
          }
        }
      }

      // Virus movement (float up, sway via sine)
      g.viruses.forEach(v => {
        v.frame++;
        v.x += Math.sin(v.frame * 0.05) * 2;
        v.y -= v.speed;
        if (v.x < 20) v.x = 20;
        if (v.x > GAME_W - 20) v.x = GAME_W - 20;
      });
      // Detect viruses that escaped past the rooster line (penalty applied below)
      const escapedViruses = g.viruses.filter(v => v.y <= ROOSTER_Y);
      escapedViruses.forEach(() => {
        g.virusBonusTotal -= 2500;
      });
      g.viruses = g.viruses.filter(v => v.y > ROOSTER_Y);

      // Farmer's wife spawn (round 3+, once per round, mid-round)
      if (g.currentRoundIdx >= 2 && !g.wifeSpawned) {
        g.wifeSpawnMs -= 16;
        if (g.wifeSpawnMs <= 0) {
          g.wifeSpawned = true;
          g.farmersWife = {
            id: Date.now() + Math.random(),
            x: GAME_W - 30,
            startX: GAME_W - 30,
            y: FARMER_Y,
            phase: 'walking',
            speed: 1.8,
            exitTimer: 0,
            throwDistance: 80 + Math.random() * (GAME_W * 0.4),
          };
          if (!g.wifeWarningShown) {
            g.wifeWarningShown = true;
            setWifeWarning(true);
            setTimeout(() => setWifeWarning(false), 3000);
          }
        }
      }

      // Wife walking + throw
      if (g.farmersWife) {
        const wf = g.farmersWife;
        if (wf.phase === 'walking') {
          wf.x -= wf.speed;
          if (wf.startX - wf.x >= wf.throwDistance) {
            const panSpeed = 1.5 * (1 + (g.currentRoundIdx / 9) * 4);
            g.fryingPans.push({
              id: Date.now() + Math.random(),
              x: wf.x,
              y: wf.y - 30,
              speed: panSpeed,
            });
            wf.phase = 'thrown';
            wf.exitTimer = 30;
          }
        } else if (wf.phase === 'thrown') {
          wf.x -= wf.speed;
          wf.exitTimer--;
          if (wf.exitTimer <= 0 || wf.x < -40) {
            g.farmersWife = null;
          }
        }
      }

      // Frying pan movement
      g.fryingPans.forEach(p => {
        p.y -= p.speed;
      });
      // Detect frying pans that escaped past rooster line
      const escapedPans = g.fryingPans.filter(p => p.y <= ROOSTER_Y);
      escapedPans.forEach(p => {
        g.fryingPanBonusTotal -= 5000;
        setFryingPanEscapeText({ x: p.x, y: ROOSTER_Y });
        setTimeout(() => setFryingPanEscapeText(null), 1500);
      });
      g.fryingPans = g.fryingPans.filter(p => p.y > ROOSTER_Y);

      // Super Hawk spawn (round 4+, once per round, early)
      if (g.currentRoundIdx >= 0 && !g.hawksSpawned) {
        g.hawkSpawnMs -= 16;
        if (g.hawkSpawnMs <= 0) {
          g.hawksSpawned = true;
          const hawkSpeed = 0.75 * (1 + (g.currentRoundIdx / 9) * 4) * 0.8;
          g.hawks = [
            { id: Date.now() + Math.random(), x: 20, y: GAME_H, frame: 0, speed: hawkSpeed, facing: 'right' },
            { id: Date.now() + Math.random() + 0.1, x: GAME_W - 20, y: GAME_H, frame: 30, speed: hawkSpeed, facing: 'left' },
          ];
          if (!g.hawkWarningShown) {
            g.hawkWarningShown = true;
            setHawkWarning(true);
            setTimeout(() => setHawkWarning(false), 3000);
          }
        }
      }

      // Hawk movement (rise upward with sway)
      g.hawks.forEach(h => {
        h.frame++;
        h.x += Math.sin(h.frame * 0.05) * 4;
        h.y -= h.speed;
        if (h.x < 20) h.x = 20;
        if (h.x > GAME_W - 20) h.x = GAME_W - 20;
      });
      // Detect hawks that escaped past rooster line
      const escapedHawks = g.hawks.filter(h => h.y <= ROOSTER_Y);
      let hawkEscapedThisTick = false;
      escapedHawks.forEach(h => {
        g.hawkBonusTotal -= 5000;
        setHawkEscapeText({ x: h.x, y: ROOSTER_Y });
        setTimeout(() => setHawkEscapeText(null), 1500);
        hawkEscapedThisTick = true;
      });
      if (hawkEscapedThisTick && g.chickenCount > 1) {
        g.chickenCount = Math.max(g.chickenCount - 1, 1);
        setChickenCount(g.chickenCount);
        setChickenMessage('LOST A CHICKEN!');
        setTimeout(() => setChickenMessage(null), 2000);
      }
      g.hawks = g.hawks.filter(h => h.y > ROOSTER_Y);

      // Collision: poop hits weapon
      const newExplosions = [];
      const hitPoopIds = new Set();
      const hitWeaponIds = new Set();

      g.weapons.forEach(w => {
        if (!w.active) return;
        g.poops.forEach(p => {
          if (hitPoopIds.has(p.id)) return;
          const dx = p.x - w.x;
          const dy = p.y - w.y;
          if (Math.sqrt(dx * dx + dy * dy) < 28) {
            hitPoopIds.add(p.id);
            hitWeaponIds.add(w.id);
            g.hits++;
            newExplosions.push({ id: Date.now() + Math.random(), x: w.x, y: w.y });
            g.bonusFlashes.push({ id: Date.now() + Math.random(), x: w.x, y: w.y - 20, text: '+1,000!', color: '#FFD700' });
          }
        });
      });

      // Collision: poop hits baby farmer
      const hitBabyIds = new Set();
      g.babyFarmers.forEach(b => {
        if (!b.alive) return;
        g.poops.forEach(p => {
          if (hitPoopIds.has(p.id) || hitBabyIds.has(b.id)) return;
          const dx = p.x - b.x;
          const dy = p.y - b.y;
          if (Math.sqrt(dx * dx + dy * dy) < 20) {
            hitPoopIds.add(p.id);
            hitBabyIds.add(b.id);
            g.babyFarmersHit++;
            newExplosions.push({ id: Date.now() + Math.random(), x: b.x, y: b.y });
            g.bonusFlashes.push({ id: Date.now() + Math.random(), x: b.x, y: b.y - 20 });
          }
        });
      });
      g.babyFarmers = g.babyFarmers.map(b => hitBabyIds.has(b.id) ? { ...b, alive: false } : b);
      setTimeout(() => { g.bonusFlashes = []; }, 800);

      // Collision: poop hits super weapons
      g.superWeapons = g.superWeapons.map(s => {
        if (s.hit) return s;
        let updated = s;
        g.poops.forEach(p => {
          if (hitPoopIds.has(p.id) || updated.hit) return;
          const dx = p.x - s.x;
          const dy = p.y - s.y;
          if (Math.sqrt(dx * dx + dy * dy) < 60) {
            hitPoopIds.add(p.id);
            updated = { ...s, hit: true };
            g.hits += 2;
            newExplosions.push({ id: Date.now() + Math.random(), x: s.x, y: s.y });
            newExplosions.push({ id: Date.now() + Math.random() + 0.1, x: s.x - 30, y: s.y - 30 });
            newExplosions.push({ id: Date.now() + Math.random() + 0.2, x: s.x + 30, y: s.y + 30 });
            g.bonusFlashes.push({ id: Date.now() + Math.random(), x: s.x, y: s.y - 30, text: '+2,000!', color: '#FFD700' });
          }
        });
        return updated;
      });

      // Collision: poop hits fox
      if (g.fox) {
        for (let i = 0; i < g.poops.length; i++) {
          const p = g.poops[i];
          if (hitPoopIds.has(p.id)) continue;
          const dx = p.x - g.fox.x;
          const dy = p.y - g.fox.y;
          if (Math.abs(dx) < 30 && Math.abs(dy) < 20) {
            hitPoopIds.add(p.id);
            g.foxBonusTotal += 5000;
            newExplosions.push({ id: Date.now() + Math.random(), x: g.fox.x, y: g.fox.y });
            const fx = g.fox.x, fy = g.fox.y;
            g.fox = null;
            setFoxBonusText({ x: fx, y: fy });
            setTimeout(() => setFoxBonusText(null), 1500);
            break;
          }
        }
      }

      // Collision: poop hits virus
      const hitVirusIds = new Set();
      g.viruses.forEach(v => {
        if (hitVirusIds.has(v.id)) return;
        for (let i = 0; i < g.poops.length; i++) {
          const p = g.poops[i];
          if (hitPoopIds.has(p.id)) continue;
          const dx = p.x - v.x;
          const dy = p.y - v.y;
          if (Math.sqrt(dx * dx + dy * dy) < 25) {
            hitPoopIds.add(p.id);
            hitVirusIds.add(v.id);
            g.virusBonusTotal += 2500;
            newExplosions.push({ id: Date.now() + Math.random(), x: v.x, y: v.y });
            g.bonusFlashes.push({ id: Date.now() + Math.random(), x: v.x, y: v.y - 20 });
            const vx = v.x, vy = v.y;
            setVirusBonusText({ x: vx, y: vy });
            setTimeout(() => setVirusBonusText(null), 1500);
            break;
          }
        }
      });
      g.viruses = g.viruses.filter(v => !hitVirusIds.has(v.id));

      // Collision: poop hits frying pan
      const hitPanIds = new Set();
      g.fryingPans.forEach(p => {
        if (hitPanIds.has(p.id)) return;
        for (let i = 0; i < g.poops.length; i++) {
          const poop = g.poops[i];
          if (hitPoopIds.has(poop.id)) continue;
          const dx = poop.x - p.x;
          const dy = poop.y - p.y;
          if (Math.sqrt(dx * dx + dy * dy) < 28) {
            hitPoopIds.add(poop.id);
            hitPanIds.add(p.id);
            g.fryingPanBonusTotal += 5000;
            newExplosions.push({ id: Date.now() + Math.random(), x: p.x, y: p.y });
            g.bonusFlashes.push({ id: Date.now() + Math.random(), x: p.x, y: p.y - 20 });
            const px = p.x, py = p.y;
            setFryingPanBonusText({ x: px, y: py });
            setTimeout(() => setFryingPanBonusText(null), 1500);
            break;
          }
        }
      });
      g.fryingPans = g.fryingPans.filter(p => !hitPanIds.has(p.id));

      // Collision: poop hits hawk
      const hitHawkIds = new Set();
      g.hawks.forEach(h => {
        if (hitHawkIds.has(h.id)) return;
        for (let i = 0; i < g.poops.length; i++) {
          const poop = g.poops[i];
          if (hitPoopIds.has(poop.id)) continue;
          const dx = poop.x - h.x;
          const dy = poop.y - h.y;
          if (Math.sqrt(dx * dx + dy * dy) < 30) {
            hitPoopIds.add(poop.id);
            hitHawkIds.add(h.id);
            g.hawkBonusTotal += 5000;
            g.hawkHitsThisRound++;
            newExplosions.push({ id: Date.now() + Math.random(), x: h.x, y: h.y });
            g.bonusFlashes.push({ id: Date.now() + Math.random(), x: h.x, y: h.y - 20, text: '+5,000!', color: '#FFA500' });
            const hx = h.x, hy = h.y;
            setHawkBonusText({ x: hx, y: hy });
            setTimeout(() => setHawkBonusText(null), 1500);
            if (g.hawkHitsThisRound >= 2 && !g.bothHawksHitThisRound) {
              g.bothHawksHitThisRound = true;
              const prev = g.chickenCount;
              g.chickenCount = Math.min(g.chickenCount + 1, 2);
              setChickenCount(g.chickenCount);
              if (g.chickenCount > prev) {
                setChickenMessage('🐔🐔 DOUBLE CHICKEN!');
                setTimeout(() => setChickenMessage(null), 2500);
              }
            }
            break;
          }
        }
      });
      g.hawks = g.hawks.filter(h => !hitHawkIds.has(h.id));

      escapedViruses.forEach(v => {
        newExplosions.push({ id: Date.now() + Math.random(), x: v.x, y: ROOSTER_Y, text: '-2500', color: '#ff4444' });
        setVirusEscapeText({ x: v.x, y: ROOSTER_Y });
        setTimeout(() => setVirusEscapeText(null), 1500);
      });

      g.weapons = g.weapons.map(w => hitWeaponIds.has(w.id) ? { ...w, active: false } : w);
      g.poops = g.poops.filter(p => !hitPoopIds.has(p.id));
      g.explosions = [...newExplosions];
      setTimeout(() => { g.explosions = []; }, 300);

      // Super weapon boundary check — player loses if any reaches the rooster
      if (g.superWeapons.some(s => !s.hit && s.y <= ROOSTER_Y)) {
        g.gameOverTriggered = true;
        endGame(false);
        return;
      }

      // Round win check — all weapons thrown AND all spawned super weapons are hit
      if (g.weaponsThrown >= g.weaponsRequired && g.superWeapons.length > 0 && g.superWeapons.every(s => s.hit) && !g.roundWinTriggered) {
        g.roundWinTriggered = true;
        clearInterval(g.roundTimer);
        setTimeout(() => {
          if (g.currentRoundIdx >= 9) {
            g.gameOverTriggered = true;
            endGame(true);
          } else {
            setGameState('roundwin');
          }
        }, 2000);
      }

      // Remove inactive weapons
      g.weapons = g.weapons.filter(w => w.active && w.y < GAME_H);

      setRoosterX(g.roosterX);
      setPoops([...g.poops]);
      setWeapons([...g.weapons]);
      setExplosions([...g.explosions]);
      setBabyFarmers([...g.babyFarmers]);
      setBonusFlashes([...g.bonusFlashes]);
      setSuperWeapons([...g.superWeapons]);
      setFox(g.fox ? { ...g.fox } : null);
      setViruses([...g.viruses]);
      setFarmersWife(g.farmersWife ? { ...g.farmersWife } : null);
      setFryingPans([...g.fryingPans]);
      setHawks([...g.hawks]);
      const calcLiveScore = () => {
        const acc = g.shots > 0 ? (g.hits / g.shots) * 100 : 0;
        const multiplier =
          acc >= 80 ? 2.5 :
          acc >= 75 ? 2.4 :
          acc >= 70 ? 2.3 :
          acc >= 65 ? 2.2 :
          acc >= 60 ? 2.1 :
          acc >= 55 ? 2.0 :
          acc >= 50 ? 1.9 :
          acc >= 45 ? 1.8 :
          acc >= 40 ? 1.7 :
          acc >= 35 ? 1.6 :
          acc >= 30 ? 1.5 :
          acc >= 25 ? 1.4 :
          acc >= 20 ? 1.3 :
          acc >= 15 ? 1.2 :
          acc >= 10 ? 1.1 : 1.0;
        let roundPts = 0;
        for (let i = 0; i < Math.min(currentRound, 10); i++) roundPts += ROUND_POINTS[i];
        return Math.round((roundPts + g.hits * 1000 + g.babyFarmersHit * 500 + g.foxBonusTotal + g.virusBonusTotal + (g.fryingPanBonusTotal || 0) + (g.hawkBonusTotal || 0)) * multiplier);
      };
      setLiveScore(calcLiveScore());
    }, 16);

    return () => clearInterval(loop);
  }, [gameState, showStartOverlay, gameReady, endGame]);

  // Keyboard
  useEffect(() => {
    const down = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      gameRef.current.keys[e.key] = true;
      if (e.key === ' ') e.preventDefault();
    };
    const up = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      gameRef.current.keys[e.key] = false;
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); };
  }, []);

  // Pause game loop when tab is hidden (prevents weapon pile-up on mobile app switch)
  useEffect(() => {
    const handleVisibility = () => {
      gameRef.current.paused = document.hidden;
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Mobile fullscreen — request when entering 'playing', exit when leaving
  useEffect(() => {
    if (!isMobile) return;
    if (gameState === 'playing') {
      const el = document.documentElement;
      try {
        if (el.requestFullscreen) {
          el.requestFullscreen().catch(() => {});
        } else if (el.webkitRequestFullscreen) {
          el.webkitRequestFullscreen();
        }
      } catch (e) {}
    } else {
      try {
        if (document.exitFullscreen && document.fullscreenElement) {
          document.exitFullscreen().catch(() => {});
        } else if (document.webkitExitFullscreen && document.webkitFullscreenElement) {
          document.webkitExitFullscreen();
        }
      } catch (e) {}
    }
  }, [isMobile, gameState]);

  // Mobile touch controls — attach as native non-passive listeners so preventDefault works
  useEffect(() => {
    if (!isMobile) return;
    if (gameState !== 'playing') return;
    const lb = leftBtnRef.current;
    const rb = rightBtnRef.current;
    const sb = shootBtnRef.current;
    if (!lb || !rb || !sb) return;

    const onLeftDown = (e) => { e.preventDefault(); gameRef.current.keys['ArrowLeft'] = true; };
    const onLeftUp = (e) => { e.preventDefault(); gameRef.current.keys['ArrowLeft'] = false; };
    const onRightDown = (e) => { e.preventDefault(); gameRef.current.keys['ArrowRight'] = true; };
    const onRightUp = (e) => { e.preventDefault(); gameRef.current.keys['ArrowRight'] = false; };
    const onShootDown = (e) => { e.preventDefault(); gameRef.current.keys[' '] = true; };
    const onShootUp = (e) => { e.preventDefault(); gameRef.current.keys[' '] = false; };

    // Global safety net: when ALL fingers leave the screen, clear movement+shoot keys.
    // Catches cases where a button's own touchend never fires (iOS scroll, browser chrome,
    // finger slides off the button). Guards on e.touches.length === 0 so multi-touch is preserved.
    const clearKeysIfNoTouches = (e) => {
      if (e.touches && e.touches.length === 0) {
        gameRef.current.keys['ArrowLeft'] = false;
        gameRef.current.keys['ArrowRight'] = false;
        gameRef.current.keys[' '] = false;
      }
    };

    lb.addEventListener('touchstart', onLeftDown, { passive: false });
    lb.addEventListener('touchend', onLeftUp, { passive: false });
    lb.addEventListener('touchcancel', onLeftUp, { passive: false });
    rb.addEventListener('touchstart', onRightDown, { passive: false });
    rb.addEventListener('touchend', onRightUp, { passive: false });
    rb.addEventListener('touchcancel', onRightUp, { passive: false });
    sb.addEventListener('touchstart', onShootDown, { passive: false });
    sb.addEventListener('touchend', onShootUp, { passive: false });
    sb.addEventListener('touchcancel', onShootUp, { passive: false });
    window.addEventListener('touchend', clearKeysIfNoTouches);
    window.addEventListener('touchcancel', clearKeysIfNoTouches);

    return () => {
      lb.removeEventListener('touchstart', onLeftDown);
      lb.removeEventListener('touchend', onLeftUp);
      lb.removeEventListener('touchcancel', onLeftUp);
      rb.removeEventListener('touchstart', onRightDown);
      rb.removeEventListener('touchend', onRightUp);
      rb.removeEventListener('touchcancel', onRightUp);
      sb.removeEventListener('touchstart', onShootDown);
      sb.removeEventListener('touchend', onShootUp);
      sb.removeEventListener('touchcancel', onShootUp);
      window.removeEventListener('touchend', clearKeysIfNoTouches);
      window.removeEventListener('touchcancel', clearKeysIfNoTouches);
    };
  }, [isMobile, gameState]);

  useEffect(() => { return () => stopMusic(); }, []);

  useEffect(() => {
    const oracleUrl = process.env.REACT_APP_ORACLE_URL || 'http://localhost:3002';
    const fetchBonanzaPool = async () => {
      try {
        const res = await fetch(`${oracleUrl}/bonanza/status`);
        if (!res.ok) return;
        const data = await res.json();
        if (typeof data.poolBalance === 'number') {
          setBonanzaPool(data.poolBalance);
        }
      } catch (err) {
        // oracle unreachable — keep last known value
      }
    };
    fetchBonanzaPool();
    const interval = setInterval(fetchBonanzaPool, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const loadPool = () => {
      try {
        const stored = localStorage.getItem('chickenDropPool');
        if (stored) setChickenDropPool(parseFloat(stored));
      } catch(e) {}
    };
    loadPool();
    window.addEventListener('storage', loadPool);
    window.addEventListener('focus', loadPool);
    return () => {
      window.removeEventListener('storage', loadPool);
      window.removeEventListener('focus', loadPool);
    };
  }, []);

  useEffect(() => {
    if (gameState !== 'roundwin') return;
    const roundIdx = currentRound;
    setRoundwinTimer(4);
    const tick = setInterval(() => {
      setRoundwinTimer(prev => {
        if (prev <= 1) {
          clearInterval(tick);
          startRound(roundIdx + 1);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [gameState]); // eslint-disable-line

  useEffect(() => {
    if (!showStartOverlay) return;
    setStartCountdown(3);
    const timer = setInterval(() => {
      setStartCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          setShowStartOverlay(false);
          gameRef.current.showStartOverlay = false;
          setGameReady(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [showStartOverlay]);

  useEffect(() => {
    if (gameState !== 'tryComplete') return;
    setTryRedirectTimer(20);
    const countdown = setInterval(() => {
      setTryRedirectTimer(prev => {
        if (prev <= 1) {
          clearInterval(countdown);
          stopMusic();
          setIsTryMode(false);
          setGameState('intro');
          setPaid(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(countdown);
  }, [gameState]);

  useEffect(() => {
    if (gameState !== 'dead' && gameState !== 'won') return;
    setAutoRedirectTimer(60);
    const countdown = setInterval(() => {
      setAutoRedirectTimer(prev => {
        if (prev <= 1) {
          clearInterval(countdown);
          navigate(selectedGameType ? GAME_CONFIGS[selectedGameType].route : '/drop/mega', { state: { pickSquare: true, wallet: effectivePublicKey, squaresEarned: 1 } });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(countdown);
  }, [gameState]);

  useEffect(() => {
    if (effectivePublicKey) {
      const agreed = localStorage.getItem(`termsAgreed_${effectivePublicKey}`);
      if (!agreed) {
        setShowTermsModal(true);
      }
    }
  }, [effectivePublicKey]);

  // Human check — bounce animation + countdown
  useEffect(() => {
    if (!showHumanCheck || humanCheckResult !== null) return;
    const BOX_W = 320, BOX_H = 200, CHICK_SIZE = 44;
    bounceRef.current = { x: 130, y: 80, vx: 2.16, vy: 1.54 };
    setBouncePos({ x: 130, y: 80 });
    setHumanCheckTimer(7);
    clicksUsedRef.current = 0;
    setClicksUsed(0);

    const loopId = setInterval(() => {
      if (loopId !== humanCheckLoopRef.current) return;
      const b = bounceRef.current;
      let nx = b.x + b.vx, ny = b.y + b.vy, nvx = b.vx, nvy = b.vy;
      if (nx <= 0 || nx >= BOX_W - CHICK_SIZE) { nvx = -nvx; nx = Math.max(0, Math.min(nx, BOX_W - CHICK_SIZE)); }
      if (ny <= 0 || ny >= BOX_H - CHICK_SIZE) { nvy = -nvy; ny = Math.max(0, Math.min(ny, BOX_H - CHICK_SIZE)); }
      bounceRef.current = { x: nx, y: ny, vx: nvx, vy: nvy };
      setBouncePos({ x: nx, y: ny });
    }, 16);
    humanCheckLoopRef.current = loopId;

    let t = 7;
    const intervalId = setInterval(() => {
      if (intervalId !== humanCheckCountdownRef.current) return;
      t--;
      setHumanCheckTimer(t);
      if (t <= 0) {
        clearInterval(humanCheckCountdownRef.current);
        clearInterval(humanCheckLoopRef.current);
        verifyAttemptsRef.current += 1;
        setVerifyAttempts(verifyAttemptsRef.current);
        if (verifyAttemptsRef.current < 4) {
          setHumanCheckResult('retry');
          setTimeout(() => setHumanCheckResult(null), 1500);
        } else {
          setHumanCheckResult('missed');
          setTimeout(() => {
            const nextState = pendingEntryRef.current?.nextState || 'dead';
            setShowHumanCheck(false);
            setGameState(nextState);
            pendingEntryRef.current = null;
            setPendingEntry(null);
            setHumanCheckResult(null);
            verifyAttemptsRef.current = 0;
            setVerifyAttempts(0);
          }, 2000);
        }
      }
    }, 1000);
    humanCheckCountdownRef.current = intervalId;

    return () => {
      clearInterval(humanCheckLoopRef.current);
      clearInterval(humanCheckCountdownRef.current);
    };
  }, [showHumanCheck, humanCheckResult]); // eslint-disable-line

  const accuracy = score.shots > 0 ? Math.round((score.hits / score.shots) * 100) : 0;

  // ── SCREENS ──

  // ── HUMAN VERIFICATION OVERLAY ──
  if (showHumanCheck) {
    return (
      <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.96)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 9999, fontFamily: 'sans-serif', color: 'white', textAlign: 'center', padding: '20px' }}>
        {humanCheckResult === 'caught' ? (
          <>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 40, color: '#4CAF50', letterSpacing: 3, marginBottom: 8 }}>GREAT - YOU'RE HUMAN!</div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 15, color: '#aaa', maxWidth: 480, lineHeight: 1.6 }}>YOUR SCORE IS NOW REGISTERED AND IF TOP 100 YOU HAVE A CHANCE TO WIN THE BONANZA POOL!!!</div>
          </>
        ) : humanCheckResult === 'missed' ? (
          <>
            <div style={{ fontSize: 72, marginBottom: 16 }}>😤</div>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 52, color: '#E05A5A', letterSpacing: 4, marginBottom: 8 }}>TOO SLOW!</div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 16, color: '#aaa' }}>Score doesn't qualify for the Bonanza draw.</div>
          </>
        ) : humanCheckResult === 'retry' ? (
          <>
            <div style={{ fontSize: 72, marginBottom: 16 }}>😅</div>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 52, color: '#E0A05A', letterSpacing: 4, marginBottom: 8 }}>MISSED!</div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 16, color: '#aaa' }}>Attempts remaining: {4 - verifyAttempts}</div>
          </>
        ) : (
          <>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 38, color: '#F0B800', letterSpacing: 3, marginBottom: 8 }}>YOU MADE THE TOP 100!</div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 16, color: '#ccc', marginBottom: 28 }}>Prove you're human — catch the chicken!</div>
            <div
              onClick={() => {
                if (humanCheckResult !== null) return;
                clicksUsedRef.current += 1;
                setClicksUsed(clicksUsedRef.current);
                if (clicksUsedRef.current >= 5) {
                  clearInterval(humanCheckCountdownRef.current);
                  clearInterval(humanCheckLoopRef.current);
                  verifyAttemptsRef.current += 1;
                  setVerifyAttempts(verifyAttemptsRef.current);
                  if (verifyAttemptsRef.current < 4) {
                    setHumanCheckResult('retry');
                    setTimeout(() => setHumanCheckResult(null), 1500);
                  } else {
                    setHumanCheckResult('missed');
                    setTimeout(() => {
                      const nextState = pendingEntryRef.current?.nextState || 'dead';
                      setShowHumanCheck(false);
                      setGameState(nextState);
                      pendingEntryRef.current = null;
                      setPendingEntry(null);
                      setHumanCheckResult(null);
                      verifyAttemptsRef.current = 0;
                      setVerifyAttempts(0);
                    }, 2000);
                  }
                }
              }}
              style={{ position: 'relative', width: 320, height: 200, border: '3px solid #F0B800', borderRadius: 12, background: '#0a1a0a', overflow: 'hidden', marginBottom: 20, flexShrink: 0, cursor: 'pointer' }}
            >
              <div
                onClick={(e) => { e.stopPropagation(); catchChicken(); }}
                style={{ position: 'absolute', left: bouncePos.x, top: bouncePos.y, fontSize: 36, cursor: 'pointer', userSelect: 'none', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
              >
                🐔
              </div>
            </div>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 48, color: humanCheckTimer <= 2 ? '#E05A5A' : '#F0B800', letterSpacing: 4, lineHeight: 1, marginBottom: 6 }}>
              {humanCheckTimer}s
            </div>
            <div style={{ fontSize: 11, color: '#888', fontFamily: "'DM Mono', monospace" }}>
              {5 - clicksUsed} clicks remaining this attempt
            </div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, color: '#aaa', marginBottom: 6 }}>
              Attempts remaining: {4 - verifyAttempts}
            </div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: '#555' }}>
              Click the chicken before time runs out
            </div>
          </>
        )}
      </div>
    );
  }

  if (gameState === 'intro') {
    return (
      <>
      <style>{`
        ::-webkit-scrollbar { width: 24px; }
        ::-webkit-scrollbar-track { background: #1a1a1a; border-radius: 12px; }
        ::-webkit-scrollbar-thumb { background: #F0B800; border-radius: 12px; border: 3px solid #1a1a1a; }
        ::-webkit-scrollbar-thumb:hover { background: #FFE033; border: 2px solid #1a1a1a; }
        ::-webkit-scrollbar-thumb:active { background: #FFEF80; border: 2px solid #1a1a1a; }
        textarea::placeholder { color: #888; opacity: 1; }
      `}</style>
      <div style={{ minHeight: '100vh', background: '#0a0a0a', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: 'sans-serif', color: 'white' }}>
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '14px', color: '#888', letterSpacing: '3px', marginBottom: '4px', textAlign: 'center', marginTop: 24 }}>
          DEFEND THE COOP
        </div>
        <div style={{ position: 'fixed', top: '16px', right: '16px', zIndex: 1000 }}>
          <WalletMultiButton />
        </div>
        <div style={{ fontSize: 52, fontWeight: 900, color: '#F0B800', letterSpacing: 4, marginBottom: 8, textShadow: '0 0 30px rgba(240,184,0,0.5)' }}>
          🐓 CHICKEN DROPPINGS
        </div>
        <div style={{ fontSize: 14, color: '#888', marginBottom: 8, letterSpacing: 2 }}>10 ROUNDS · EASY!! STEP 1-PICK YOUR CHICKEN DROP ARENA SIZE 2. PLAY THE CHICKEN DROPPINGS GAME 3. SELECT YOUR CHICKEN DROP SQUARE, STEP 4 WIN!!</div>
        <div style={{ textAlign: 'center', marginTop: '12px', marginBottom: '4px' }}>
          <button
            onClick={() => { setIsTryMode(true); setPaid(true); startGame(); }}
            style={{ background: '#F0B800', color: '#000', border: 'none', borderRadius: '6px', padding: '6px 16px', fontSize: '11px', fontFamily: "'DM Mono', monospace", cursor: 'pointer', letterSpacing: '1px', fontWeight: 700 }}
          >
            Practice For Free
          </button>
        </div>

        <svg viewBox={`0 0 ${GAME_W} 250`} style={{ width: '100%', maxWidth: 800, height: '220px', background: '#1a2a1a', borderRadius: 12, marginBottom: 32, border: '1px solid #2a2a2a' }}>
          <rect width={GAME_W} height="250" fill="#1a2a1a" />
          <rect width={GAME_W} height="20" fill="#2d4a2d" y="230" />
          <g transform="translate(0, 50)">
            <Coop />
            <Rooster x={200} pooping={false} />
            <Farmer x={500} facing="right" />
          </g>
          <text x={GAME_W / 2} y="220" textAnchor="middle" fontSize="18" fill="#F0B800" fontFamily="sans-serif" fontWeight="bold">← → Move · SPACE to Poop!</text>
        </svg>

        {!publicKey ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#888', marginBottom: 12, fontSize: 13 }}>Connect your wallet to play</div>
            <WalletMultiButton />
          </div>
        ) : (
          <>
            {showRegisterConfirm && (
              <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ background: '#1a1a1a', border: '1px solid #F0B800', borderRadius: 16, padding: '36px 40px', maxWidth: 420, width: '90%', textAlign: 'center' }}>
                  <div style={{ fontSize: 40, marginBottom: 16 }}>🐔</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#F0B800', marginBottom: 16, letterSpacing: 1 }}>CONFIRM REGISTRATION</div>
                  <div style={{ fontSize: 14, color: '#ccc', marginBottom: 24, lineHeight: 1.6 }}>
                    You are about to pay <span style={{ color: '#F0B800', fontWeight: 700 }}>0.006 SOL</span> to register your wallet on-chain.
                    <br /><br />
                    This is a <strong>one-time registration & anti-spam fee</strong> that creates your permanent player account. You will never be charged again on this wallet.
                    <br /><br />
                    <span style={{ color: '#888', fontSize: 12 }}>After clicking Confirm, your Solana wallet will ask you to approve the transaction.</span>
                  </div>
                  <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                    <button
                      onClick={() => setShowRegisterConfirm(false)}
                      style={{ background: '#333', color: '#aaa', border: 'none', borderRadius: 8, padding: '12px 28px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => { setShowRegisterConfirm(false); registerWallet(); }}
                      disabled={isRegistering}
                      style={{ background: '#F0B800', color: '#000', border: 'none', borderRadius: 8, padding: '12px 28px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
                    >
                      ✓ Confirm & Open Wallet
                    </button>
                  </div>
                </div>
              </div>
            )}
            {publicKey && isRegistered === false && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', textAlign: 'center' }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>🐔</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#F0B800', marginBottom: 8, letterSpacing: 2 }}>WELCOME TO THE COOP!</div>
                <div style={{ fontSize: 14, color: '#aaa', marginBottom: 8 }}>First time here? You need to make a small one time payment to play.</div>
                <div style={{ fontSize: 13, color: '#888', marginBottom: 24 }}>This is a <span style={{ color: '#F0B800' }}>one-time Solana platform registration & anti-spam fee</span> — you will never be charged this fee again on this wallet.</div>
                <button
                  onClick={() => setShowRegisterConfirm(true)}
                  disabled={isRegistering}
                  style={{ background: isRegistering ? '#333' : '#F0B800', color: isRegistering ? '#666' : '#000', border: 'none', borderRadius: 8, padding: '14px 40px', fontSize: 16, fontWeight: 700, cursor: isRegistering ? 'not-allowed' : 'pointer', letterSpacing: 2, marginBottom: 12 }}
                >
                  {isRegistering ? '⏳ REGISTERING...' : '🐔 REGISTER & PLAY (0.006 SOL)'}
                </button>
                {registrationError && (
                  <div style={{ color: '#ff4444', fontSize: 12, marginTop: 8 }}>⚠️ {registrationError}</div>
                )}
                <div style={{ fontSize: 11, color: '#555', marginTop: 8 }}>Already registered on another device? <span style={{ color: '#F0B800', cursor: 'pointer' }} onClick={() => window.location.reload()}>Refresh to check</span></div>
              </div>
            )}
            {publicKey && isChecking && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px', color: '#aaa', fontSize: 14 }}>
                🔍 Checking your player account...
              </div>
            )}
            {isRegistered === true && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '20px' }}>
            {showWelcomeBack && publicKey && (
              <div style={{ background: '#1a2a1a', border: '1px solid #4a8a4a', borderRadius: 8, padding: '10px 24px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: '#7aff7a' }}>
                <span>✅</span>
                <span>Welcome back! Wallet verified on-chain.</span>
              </div>
            )}
            <div style={{ marginBottom: 20, width: '100%', maxWidth: 900 }}>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 14, color: '#cccccc', marginBottom: 14, textAlign: 'center', lineHeight: 1.6 }}>
                Play Chicken Droppings and WIN the Chicken Drop pool !!<br/>
                A new WINNER is selected each time all Chicken Drop squares are filled.<br/>
                Play the game, choose a square and WIN! Future airdrop will only goto wallets that play! See Terms page for airdrop details!
              </div>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, color: '#F0B800', letterSpacing: 2, marginBottom: 12, textAlign: 'center' }}>
                SELECT YOUR CHICKEN DROP ARENA
              </div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                {Object.values(GAME_CONFIGS).filter(c => c.entryFee !== 0).map(config => (
                  <div
                    key={config.id}
                    onClick={() => setSelectedGameType(config.id)}
                    style={{
                      background: selectedGameType === config.id ? 'rgba(240,184,0,0.15)' : '#111',
                      border: selectedGameType === config.id ? '2px solid #F0B800' : '1px solid #2a2a2a',
                      borderRadius: 12,
                      padding: '16px 20px',
                      cursor: 'pointer',
                      textAlign: 'center',
                      minWidth: 160,
                      transition: 'all 0.2s',
                    }}
                  >
                    <div style={{ fontSize: 24, marginBottom: 4 }}>{config.emoji}</div>
                    <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 18, color: '#F0B800', letterSpacing: 2 }}>
                      {config.label}
                    </div>
                    <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, color: '#cccccc', marginTop: 6, fontWeight: 'bold' }}>
                      {config.totalSquares} squares · {config.entryFee.toFixed(2)} SOL entry
                    </div>
                    <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 14, color: '#4CAF50', marginTop: 6 }}>
                      WIN UP TO {((config.entryFee * config.poolShare) + ((config.totalSquares - 1) * config.directBuyFee * config.poolShare)).toFixed(3)} SOL
                    </div>
                    <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: '#F0B800', marginTop: 4 }}>
                      Max ~{config.maxMultiplier}x 🔥
                    </div>
                    {selectedGameType === config.id && (
                      <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: '#4CAF50', marginTop: 6, letterSpacing: 1 }}>
                        ✓ SELECTED
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {!paid ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                {!selectedGameType && (
                  <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: '#888', marginBottom: 4 }}>
                    Please select a game above first
                  </div>
                )}
                <button
                  onClick={async () => {
                    if (!selectedGameType) return;
                    if (!publicKey) {
                      alert('Please connect your Solana wallet first');
                      return;
                    }
                    setPaymentStatus('pending');
                    setPaymentError(null);
                    try {
                      const config = GAME_CONFIGS[selectedGameType];
                      const amountLamports = Math.round(config.entryFee * LAMPORTS_PER_SOL);
                      const poolShare = Math.round(amountLamports * config.poolShare);
                      const devShare = Math.round(amountLamports * config.devShare);
                      const bonanzaShare = amountLamports - poolShare - devShare;
                      const poolWallet = new PublicKey(WALLET_ADDRESSES.pools[selectedGameType]);
                      const devWallet = new PublicKey(WALLET_ADDRESSES.dev);
                      const bonanzaWallet = new PublicKey(WALLET_ADDRESSES.bonanza);
                      const MEMO_PROGRAM_ID = new PublicKey('MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr');
                      const memoIx = new TransactionInstruction({
                        keys: [{ pubkey: publicKey, isSigner: true, isWritable: false }],
                        programId: MEMO_PROGRAM_ID,
                        data: Buffer.from(`CD|${selectedGameType}|entry`, 'utf8'),
                      });
                      const transaction = new Transaction().add(
                        SystemProgram.transfer({ fromPubkey: publicKey, toPubkey: poolWallet, lamports: poolShare }),
                        SystemProgram.transfer({ fromPubkey: publicKey, toPubkey: devWallet, lamports: devShare }),
                        SystemProgram.transfer({ fromPubkey: publicKey, toPubkey: bonanzaWallet, lamports: bonanzaShare }),
                        memoIx
                      );
                      const { blockhash } = await connection.getLatestBlockhash();
                      transaction.recentBlockhash = blockhash;
                      transaction.feePayer = publicKey;
                      const signature = await sendTransaction(transaction, connection);
                      await connection.confirmTransaction(signature, 'confirmed');
                      // Payment confirmed - update local state
                      setPaymentStatus('confirmed');
                      setIsTryMode(false);
                      setPaid(true);
                    } catch (err) {
                      console.error('Payment error:', err);
                      setPaymentStatus('error');
                      setPaymentError(err.message || 'Payment failed. Please try again.');
                    }
                  }}
                  disabled={paymentStatus === 'pending'}
                  style={{ background: selectedGameType ? '#F0B800' : '#333', color: selectedGameType ? '#000' : '#666', border: 'none', borderRadius: 8, padding: '14px 40px', fontSize: 16, fontWeight: 700, cursor: selectedGameType ? 'pointer' : 'not-allowed', letterSpacing: 2 }}
                >
                  {paymentStatus === 'pending' ? '⏳ CONFIRMING...' : 'CONFIRM & PLAY'}
                </button>
                {paymentStatus === 'error' && (
                  <div style={{ color: '#ff4444', fontSize: 12, marginTop: 8, textAlign: 'center', maxWidth: 300 }}>
                    ⚠️ {paymentError}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: "'DM Mono', monospace", fontSize: 12, color: oracleStatus === 'online' ? '#4CAF50' : oracleStatus === 'offline' ? '#E05A5A' : '#888' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: oracleStatus === 'online' ? '#4CAF50' : oracleStatus === 'offline' ? '#E05A5A' : '#888', display: 'inline-block' }} />
                  {oracleStatus === 'online' ? 'Server Connected' : oracleStatus === 'offline' ? 'Server Offline — try again shortly' : 'Checking server...'}
                </div>
                <button
                  onClick={startGame}
                  disabled={oracleStatus !== 'online'}
                  style={{ background: '#4CAF50', color: 'white', border: 'none', borderRadius: 8, padding: '14px 40px', fontSize: 16, fontWeight: 700, cursor: oracleStatus === 'online' ? 'pointer' : 'not-allowed', letterSpacing: 2, opacity: oracleStatus === 'online' ? 1 : 0.5 }}
                >
                  START GAME!
                </button>
              </div>
            )}
          </div>
            )}
          </>
        )}

        <div style={{ background: '#111', border: '1px solid #2a2a2a', borderRadius: 12, padding: '14px 32px', marginBottom: 24, maxWidth: 750, textAlign: 'center' }}>
          <div style={{ fontSize: '15px', color: '#aaa', lineHeight: 1.8 }}>
            <div style={{ marginBottom: '10px' }}>
              1. The farmer is throwing tools to break into the ladies' coop! Move your chicken with <span style={{ color: '#F0B800' }}>← →</span> and block the evil farmer with your super stool. Blast your super stool with the <span style={{ color: '#F0B800' }}>SPACEBAR</span>.
            </div>
            <div style={{ marginBottom: '10px' }}>
              2. Each play earns a square in the <span style={{ color: '#F0B800' }}>Chicken Drop</span> for a chance to win the prize pool!!
            </div>
            <div>
              3. <span style={{ color: '#4CAF50', fontWeight: 700 }}>ALSO</span> the top <span style={{ color: '#F0B800' }}>100 high scores</span> earn a spot in the mega progressive <span style={{ color: '#F0B800' }}>BONANZA draw</span> with a chance to win a poop load of SOL!!
            </div>
          </div>
        </div>


        <div style={{ background: 'rgba(240,184,0,0.08)', border: '1px solid rgba(240,184,0,0.3)', borderRadius: 10, padding: '10px 20px', marginBottom: 20, textAlign: 'center' }}>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '15px', color: '#F0B800', letterSpacing: '2px', marginBottom: 4, fontWeight: 700 }}>
            🎰 BONANZA POOL
          </div>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', color: '#F0B800', letterSpacing: '3px' }}>
            CURRENTLY AT: {bonanzaPool.toFixed(3)} SOL
          </div>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '14px', color: '#cccccc', marginTop: 4 }}>
            FREE bonus BONANZA draw is triggered once pool reaches 25 SOL<br/>Only the Top 100 scores qualify!
          </div>
          <div style={{ color: '#E05A5A', fontFamily: "'DM Mono', monospace", fontSize: '12px', marginTop: 4 }}>
            No bots allowed! 🚫🤖 Maximum 5 Top Scores per wallet can be registered.
          </div>
        </div>

        {process.env.NODE_ENV === 'development' && (
          <button
            onClick={() => { setPaid(true); startGame(); }}
            style={{ marginTop: '10px', marginBottom: '10px', background: '#c0392b', color: 'white', border: '1px solid #e74c3c', borderRadius: '8px', padding: '12px 32px', fontSize: '14px', fontFamily: "'DM Mono', monospace", cursor: 'pointer', letterSpacing: '1px', display: 'block', width: '100%', fontWeight: 700 }}
          >
            🧪 TEST PLAY (skip payment)
          </button>
        )}
        {process.env.NODE_ENV === 'development' && (
          <button
            onClick={() => {
              setScore({ rounds: 10, timeAlive: 300, shots: 50, hits: 45, accuracy: 90, finalScore: 999999 });
              setGameState('won');
            }}
            style={{ marginTop: '6px', background: '#1a472a', color: '#4CAF50', border: '1px solid #2d6a3f', borderRadius: '8px', padding: '10px 28px', fontSize: '13px', fontFamily: "'DM Mono', monospace", cursor: 'pointer', letterSpacing: '1px', display: 'block', width: '100%' }}
          >
            🏆 TEST WIN SCREEN
          </button>
        )}
        {process.env.NODE_ENV === 'development' && (
          <button
            onClick={() => {
              setScore({ rounds: 3, timeAlive: 120, shots: 20, hits: 12, accuracy: 60, finalScore: 125000 });
              setGameState('dead');
            }}
            style={{ marginTop: '6px', background: '#4a1a1a', color: '#E05A5A', border: '1px solid #7a2a2a', borderRadius: '8px', padding: '10px 28px', fontSize: '13px', fontFamily: "'DM Mono', monospace", cursor: 'pointer', letterSpacing: '1px', display: 'block', width: '100%' }}
          >
            💀 TEST LOST SCREEN
          </button>
        )}
        {process.env.NODE_ENV === 'development' && (
          <button
            onClick={() => {
              const g = gameRef.current;
              g.hits = 45;
              g.shots = 55;
              g.babyFarmersHit = 8;
              g.foxBonusTotal = 5000;
              g.roundSeconds = 180;
              setCurrentRound(10);
              setPaid(true);
              paidRef.current = true;
              setIsTryMode(false);
              isTryModeRef.current = false;
              endGame(true);
            }}
            style={{ marginTop: '8px', background: '#1a1a3a', color: '#F0B800', border: '1px solid #2a2a6a', borderRadius: '8px', padding: '8px 16px', fontSize: '12px', fontFamily: "'DM Mono', monospace", cursor: 'pointer', letterSpacing: '1px', display: 'block', width: '100%' }}
          >
            🏆 TEST WIN GAME (ALL 10 ROUNDS)
          </button>
        )}
        {process.env.NODE_ENV === 'development' && (
          <button
            onClick={() => {
              setIsTryMode(true);
              setPaid(true);
              setGameState('tryComplete');
            }}
            style={{ marginTop: '8px', background: '#1a2a1a', color: '#4CAF50', border: '1px solid #2a4a2a', borderRadius: '8px', padding: '8px 16px', fontSize: '12px', fontFamily: "'DM Mono', monospace", cursor: 'pointer', letterSpacing: '1px', display: 'block', width: '100%' }}
          >
            🆓 TEST TRY MODE COMPLETE SCREEN
          </button>
        )}
        {process.env.NODE_ENV === 'development' && (
          <button
            onClick={() => {
              const dummyEntry = { shortWallet: 'Test...1234', walletAddr: 'TestWallet1111111111111111111111111111111111', finalScore: 999999, nextState: 'won' };
              pendingEntryRef.current = dummyEntry;
              setPendingEntry(dummyEntry);
              setShowHumanCheck(true);
            }}
            style={{ marginTop: '6px', background: '#1a1a2a', color: '#F0B800', border: '1px solid #F0B800', borderRadius: '8px', padding: '10px 28px', fontSize: '13px', fontFamily: "'DM Mono', monospace", cursor: 'pointer', letterSpacing: '1px', display: 'block', width: '100%' }}
          >
            🐔 TEST HUMAN VERIFICATION SCREEN
          </button>
        )}
        {process.env.NODE_ENV === 'development' && (
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
            <button
              onClick={() => navigate('/drop/mini')}
              style={{ background: '#1a1a2a', color: '#5AAAE0', border: '1px solid #2a2a5a', borderRadius: '8px', padding: '6px 14px', fontSize: '11px', fontFamily: "'DM Mono', monospace", cursor: 'pointer', letterSpacing: '1px' }}
            >
              🧪 VIEW MINI COOP (4×4)
            </button>
            <button
              onClick={() => navigate('/drop/standard')}
              style={{ background: '#1a1a2a', color: '#5AAAE0', border: '1px solid #2a2a5a', borderRadius: '8px', padding: '6px 14px', fontSize: '11px', fontFamily: "'DM Mono', monospace", cursor: 'pointer', letterSpacing: '1px' }}
            >
              🧪 VIEW STANDARD COOP (5×5)
            </button>
            <button
              onClick={() => navigate('/drop/mega')}
              style={{ background: '#1a1a2a', color: '#5AAAE0', border: '1px solid #2a2a5a', borderRadius: '8px', padding: '6px 14px', fontSize: '11px', fontFamily: "'DM Mono', monospace", cursor: 'pointer', letterSpacing: '1px' }}
            >
              🧪 VIEW MEGA COOP (10×10)
            </button>
          </div>
        )}

        <div style={{ width: '100%', maxWidth: 600, margin: '32px auto 0 auto', background: '#111', border: '1px solid #2a2a2a', borderRadius: 12, padding: 20 }}>
          <div style={{ fontSize: 12, color: '#F0B800', letterSpacing: 2, marginBottom: 12, textAlign: 'center', fontFamily: "'DM Mono', monospace" }}>
            🏆 TOP 100 — CHICKEN DROPPINGS LEADERBOARD
          </div>
          {effectivePublicKey && (() => {
            const lb = leaderboard;
            const shortW = (w) => w ? w.slice(0,4) + '...' + w.slice(-4) : '????';
            const walletEntries = lb
              .map((e, idx) => ({ e, idx }))
              .filter(({ e }) => e.wallet === shortW(effectivePublicKey) || e.fullWallet === effectivePublicKey);
            if (walletEntries.length === 0) return (
              <div style={{ background: '#1a1a1a', border: '1px solid #333', borderRadius: 6, padding: '6px 10px', marginBottom: 8, fontSize: 11, color: '#888', textAlign: 'center', fontFamily: "'DM Mono', monospace" }}>
                Not yet ranked — play Chicken Droppings to get on the board!
              </div>
            );
            const best = walletEntries.reduce((a, b) => (b.e.score > a.e.score ? b : a));
            return (
              <div style={{ background: 'rgba(240,184,0,0.15)', border: '1px solid #F0B800', borderRadius: 6, padding: '6px 10px', marginBottom: 8, fontSize: 12, display: 'flex', justifyContent: 'space-between', fontFamily: "'DM Mono', monospace" }}>
                <span style={{ color: '#F0B800', minWidth: 120 }}>Your best: #{best.idx + 1}</span>
                <span style={{ color: '#aaa', flex: 1, textAlign: 'center' }}>{best.e.wallet}</span>
                <span style={{ color: '#4CAF50', minWidth: 100, textAlign: 'right' }}>— {(best.e.score || 0).toLocaleString()} points</span>
              </div>
            );
          })()}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', fontSize: 10, color: '#666', letterSpacing: 1, borderBottom: '1px solid #2a2a2a', marginBottom: 4, fontFamily: "'DM Mono', monospace" }}>
            <span style={{ minWidth: 28 }}>#</span>
            <span style={{ flex: 1 }}>WALLET</span>
            <span style={{ marginRight: 8 }}>ROUND</span>
            <span style={{ marginRight: 8, minWidth: 40, textAlign: 'right' }}>ACC%</span>
            <span>SCORE</span>
          </div>
          <div style={{ maxHeight: 400, overflowY: 'scroll', scrollbarWidth: 'thin', scrollbarColor: '#F0B800 #1a1a1a' }}>
            {(() => {
              const lb = leaderboard;
              const shortW = (w) => w ? w.slice(0,4) + '...' + w.slice(-4) : '????';
              const playerShort = effectivePublicKey ? shortW(effectivePublicKey) : null;
              const rows = [];
              for (let i = 0; i < 100; i++) {
                const entry = lb[i];
                const isMe = entry && effectivePublicKey && (entry.wallet === playerShort || entry.fullWallet === effectivePublicKey);
                rows.push(
                  <div key={i} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '4px 8px',
                    background: isMe ? 'rgba(240,184,0,0.1)' : i % 2 === 0 ? '#111' : '#0a0a0a',
                    borderRadius: 4,
                    fontSize: 11,
                    border: isMe ? '1px solid rgba(240,184,0,0.3)' : 'none',
                    fontFamily: "'DM Mono', monospace",
                  }}>
                    <span style={{ color: i < 3 ? '#F0B800' : '#555', minWidth: 28 }}>#{i + 1}</span>
                    {entry ? (
                      <>
                        <span style={{ color: isMe ? '#F0B800' : '#aaa', flex: 1 }}>{entry.wallet}</span>
                        <span style={{ color: '#888', fontSize: 10, marginRight: 8 }}>Rd {entry.rounds || '?'}</span>
                        <span style={{ color: '#888', fontSize: 10, marginRight: 8, minWidth: 40, textAlign: 'right' }}>{Math.round(entry.accuracy || 0)}%</span>
                        <span style={{ color: i < 3 ? '#F0B800' : '#bbb' }}>{(entry.score || 0).toLocaleString()}</span>
                      </>
                    ) : (
                      <span style={{ color: '#333', flex: 1, fontStyle: 'italic' }}>no score yet</span>
                    )}
                  </div>
                );
              }
              return rows;
            })()}
          </div>
        </div>
        {/* end leaderboard */}

        {/* Footer */}
        <div style={{
          marginTop: 40,
          paddingTop: 20,
          borderTop: '1px solid #222',
          textAlign: 'center',
          color: '#aaa',
          fontSize: 11,
          fontFamily: "'DM Mono', monospace",
          letterSpacing: 1,
        }}>
          <div style={{ marginBottom: 8 }}>© 2026 Chicken Drop. All rights reserved.</div>
          <div>
            <Link to="/terms" style={{ color: '#F0B800', textDecoration: 'none', letterSpacing: 1 }}>
              Terms &amp; Conditions
            </Link>
            <span style={{ color: '#666', margin: '0 8px' }}>·</span>
            <span style={{ color: '#aaa' }}>Play responsibly · 18+ only</span>
            <span style={{ color: '#666', margin: '0 8px' }}>·</span>
            <button
              onClick={() => setShowContactModal(true)}
              style={{ background: 'none', border: 'none', color: '#F0B800', fontFamily: "'DM Mono', monospace", fontSize: 11, cursor: 'pointer', textDecoration: 'none', letterSpacing: 1, padding: 0 }}
            >
              contact
            </button>
          </div>
        </div>

      </div>

      {showTermsModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          background: 'rgba(0,0,0,0.85)', zIndex: 99999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#111', border: '2px solid #F0B800', borderRadius: 16,
            padding: '32px 40px', maxWidth: 480, width: '90%', textAlign: 'center',
          }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🐓</div>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 28, color: '#F0B800', letterSpacing: 3, marginBottom: 16 }}>
              WELCOME TO CHICKEN DROPPINGS!
            </div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, color: '#ccc', marginBottom: 24, lineHeight: 1.6 }}>
              Before you play, please confirm you agree to our Terms & Conditions.
            </div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: '#F0B800', letterSpacing: 2, marginBottom: 8 }}>
              📄 TERMS & CONDITIONS
            </div>
            <div style={{ background: '#0a0a0a', border: '1px solid #222', borderRadius: 8, padding: 16, maxHeight: 200, overflowY: 'auto', marginBottom: 16, textAlign: 'left', fontSize: 11, color: '#888', fontFamily: "'DM Mono', monospace", lineHeight: 1.6 }}>
              By using the Chicken Drop platform you agree to play responsibly. This is a pay-to-play video game involving real SOL cryptocurrency. You must be of legal age in your jurisdiction. All payments are final and non-refundable. The platform is provided as-is without warranty. See full terms on the landing page.
            </div>
            <button
              onClick={() => {
                localStorage.setItem(`termsAgreed_${effectivePublicKey}`, 'true');
                setShowTermsModal(false);
              }}
              style={{ background: '#F0B800', color: '#000', border: 'none', borderRadius: 8, padding: '14px 32px', fontSize: 16, fontWeight: 700, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: 2, cursor: 'pointer', width: '100%' }}
            >
              I AGREE — LET'S PLAY! 🐔
            </button>
          </div>
        </div>
      )}

      {showContactModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          background: 'rgba(0,0,0,0.85)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            background: '#111', border: '2px solid #F0B800', borderRadius: 16,
            padding: '32px', maxWidth: 480, width: '90%', position: 'relative'
          }}>
            <button onClick={() => { setShowContactModal(false); setContactMessage(''); setContactStatus(null); }}
              style={{ position: 'absolute', top: 12, right: 16, background: 'none', border: 'none', color: '#ccc', fontSize: 22, cursor: 'pointer' }}>✕</button>

            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 28, color: '#F0B800', letterSpacing: 3, marginBottom: 8 }}>
              CONTACT US
            </div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: '#ccc', marginBottom: 20 }}>
              Your message will be signed with your wallet to verify authenticity.
            </div>

            {!publicKey ? (
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, color: '#E05A5A', textAlign: 'center', padding: '20px 0' }}>
                Please connect your wallet to send a message.
              </div>
            ) : contactStatus === 'sent' ? (
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 14, color: '#4CAF50', textAlign: 'center', padding: '20px 0' }}>
                ✓ Message sent successfully!<br/>
                <span style={{ fontSize: 11, color: '#ccc' }}>Thank you — we will be in touch.</span>
              </div>
            ) : (
              <>
                <textarea
                  value={contactMessage}
                  onChange={e => setContactMessage(e.target.value)}
                  placeholder="Type your message here..."
                  maxLength={500}
                  style={{
                    width: '100%', minHeight: 120, background: '#1a1a1a', border: '2px solid #F0B800',
                    borderRadius: 8, padding: '12px', color: '#ffffff', fontFamily: "'DM Mono', monospace",
                    fontSize: 13, resize: 'vertical', boxSizing: 'border-box', outline: 'none'
                  }}
                />
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: '#ccc', textAlign: 'right', marginTop: 4 }}>
                  {contactMessage.length}/500
                </div>

                {contactStatus === 'error' && (
                  <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: '#E05A5A', marginTop: 8 }}>
                    ⚠ Signature cancelled or failed. Please try again.
                  </div>
                )}

                <button
                  onClick={async () => {
                    if (!contactMessage.trim()) return;
                    setContactStatus('signing');
                    try {
                      const wallet = effectivePublicKey || 'anonymous';
                      const messageToSign = `Chicken Drop Contact Message\nFrom: ${wallet}\nMessage: ${contactMessage}\nTimestamp: ${new Date().toISOString()}`;

                      if (publicKey && window.solana) {
                        const encodedMessage = new TextEncoder().encode(messageToSign);
                        await window.solana.signMessage(encodedMessage, 'utf8');
                      }

                      console.log('Signed contact message:', messageToSign);
                      setContactStatus('sent');
                      setContactMessage('');
                    } catch (err) {
                      console.error('Signature failed:', err);
                      setContactStatus('error');
                    }
                  }}
                  disabled={!contactMessage.trim() || contactStatus === 'signing'}
                  style={{
                    marginTop: 16, width: '100%', background: contactMessage.trim() ? '#F0B800' : '#333',
                    color: contactMessage.trim() ? '#000' : '#bbb', border: 'none', borderRadius: 8,
                    padding: '14px', fontSize: 15, fontWeight: 700, fontFamily: "'DM Mono', monospace",
                    cursor: contactMessage.trim() ? 'pointer' : 'not-allowed', letterSpacing: 2,
                    transition: 'all 0.2s'
                  }}
                >
                  {contactStatus === 'signing' ? '⏳ WAITING FOR SIGNATURE...' : '✉ SEND MESSAGE'}
                </button>

                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: '#ccc', marginTop: 12, textAlign: 'center' }}>
                  Wallet: {effectivePublicKey ? `${effectivePublicKey.slice(0,4)}...${effectivePublicKey.slice(-4)}` : 'Not connected'}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      </>
    );
  }

  if (gameState === 'dead') {
    return (
      <div style={{ minHeight: '100vh', background: '#0a0a0a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'sans-serif', color: 'white', gap: 40 }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ fontSize: 72, marginBottom: 8 }}>😤</div>
          <div style={{ fontSize: 52, fontWeight: 900, color: '#E05A5A', letterSpacing: 4, marginBottom: 8, textShadow: '0 0 40px rgba(224,90,90,0.8)' }}>YOU LOST!</div>
          <div style={{ fontSize: 16, color: '#888', marginBottom: 24, letterSpacing: 2 }}>BUT YOU MADE IT TO ROUND {currentRound + 1}!</div>
          <div style={{ background: '#111', border: '2px solid #E05A5A', borderRadius: 12, padding: '24px 40px', marginBottom: 32, textAlign: 'left', minWidth: 320 }}>
            <div style={{ fontSize: 13, color: '#888', marginBottom: 16, letterSpacing: 2, textAlign: 'center' }}>SCORE BREAKDOWN</div>
            <ScoreBreakdown score={score} accentColor="#E05A5A" />
          </div>
          <div style={{ fontSize: 14, marginBottom: 24, textAlign: 'center', lineHeight: 1.8 }}>
            <div style={{ color: '#F0B800', fontWeight: 700, fontSize: 15, marginBottom: 8 }}>
              🐔 You get to pick a square for a chance to win Chicken Drop!<br />
              Go pick your square for a chance to win!
            </div>
            <div style={{ color: '#F0B800', marginBottom: 4 }}>
              🎊 DON'T FORGET! The TOP 100 Chicken Droppings scores qualify for the PROGRESSIVE BONANZA Chicken Drop!
            </div>
            <span style={{ color: '#4CAF50', fontSize: 12 }}>Your score has been submitted to the leaderboard. Only top 5 scores per wallet are registered.</span>
          </div>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: '#FFFFFF', marginBottom: 8 }}>
            Auto-redirecting to pick your square in {autoRedirectTimer}s
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={() => navigate(selectedGameType ? GAME_CONFIGS[selectedGameType].route : '/drop/mega', { state: { pickSquare: true, wallet: effectivePublicKey, squaresEarned: 1 } })}
              style={{ background: '#F0B800', color: '#000', border: 'none', borderRadius: 8, padding: '12px 32px', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}
            >
              NOW GO PICK YOUR SQUARE AND WIN!! 🐔
            </button>
          </div>
        </div>
        <div style={{ background: '#111', border: '1px solid #2a2a2a', borderRadius: 12, padding: '20px', minWidth: 200, maxWidth: 520 }}>
          <LeaderboardDisplay leaderboard={leaderboard} currentWallet={effectivePublicKey} currentScore={score.finalScore} />
        </div>
      </div>
    );
  }

  if (gameState === 'roundwin') {
    const nextRound = ROUNDS[currentRound + 1];
    return (
      <div style={{ minHeight: '100vh', background: '#0a0a0a', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: 'sans-serif', color: 'white' }}>
        <div style={{ fontSize: 18, color: '#4CAF50', letterSpacing: 3, marginBottom: 8 }}>ROUND {currentRound + 1} COMPLETE!</div>
        <div style={{ fontSize: 52, fontWeight: 900, color: '#F0B800', letterSpacing: 4, marginBottom: 16 }}>YOU DONE SHAT REAL GOOD!!</div>
        {/* Show the weapon they just defeated */}
        <img src={`/images/weapons/${roundConfig.weapon}.png`} alt={roundConfig.label} style={{ width: 80, height: 80, objectFit: 'contain', marginBottom: 16, filter: 'drop-shadow(0 0 20px rgba(240,184,0,0.5))' }} />
        <div style={{ color: '#888', marginBottom: 8 }}>You defeated the {roundConfig.label}!</div>
        <div style={{ color: '#666', marginBottom: 32, fontSize: 13 }}>
          Accuracy this round: {Math.round((gameRef.current.hits / Math.max(gameRef.current.shots, 1)) * 100)}%
        </div>
        {nextRound && (
          <div style={{ background: '#111', border: '1px solid #333', borderRadius: 8, padding: '16px 32px', marginBottom: 24, textAlign: 'center' }}>
            <div style={{ color: '#888', fontSize: 12, letterSpacing: 2, marginBottom: 8 }}>NEXT UP — ROUND {currentRound + 2}</div>
            <div style={{ color: '#F0B800', fontSize: 20, fontWeight: 700 }}>{nextRound.label}</div>
            <div style={{ color: '#E05A5A', fontSize: 12, marginTop: 4 }}>Speed increases!</div>
          </div>
        )}
        <button
          onClick={() => startRound(currentRound + 1)}
          style={{ background: '#4CAF50', color: 'white', border: 'none', borderRadius: 8, padding: '14px 40px', fontSize: 16, fontWeight: 700, cursor: 'pointer', letterSpacing: 2 }}
        >
          NEXT ROUND →
        </button>
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: '#666', marginTop: 8 }}>
          Next round starting automatically in {roundwinTimer}s...
        </div>
        <button
          onClick={() => { stopMusic(); setIsTryMode(false); setGameState('intro'); setPaid(false); setCurrentRound(0); }}
          style={{ marginTop: '12px', background: '#1a0a0a', color: '#E05A5A', border: '1px solid #4a1a1a', borderRadius: '8px', padding: '10px 28px', fontSize: '13px', fontFamily: "'DM Mono', monospace", cursor: 'pointer', letterSpacing: '1px' }}
        >
          END GAME
        </button>
      </div>
    );
  }

  if (gameState === 'won') {
    return (
      <div style={{ minHeight: '100vh', background: '#0a0a0a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'sans-serif', color: 'white', gap: 40 }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ fontSize: 72, marginBottom: 8 }}>🏆</div>
          <div style={{ fontSize: 52, fontWeight: 900, color: '#F0B800', letterSpacing: 4, marginBottom: 8, textShadow: '0 0 40px rgba(240,184,0,0.8)' }}>YOU WON!</div>
          <div style={{ fontSize: 16, color: '#4CAF50', marginBottom: 24, letterSpacing: 2 }}>ALL 10 ROUNDS COMPLETE!</div>
          <div style={{ color: '#F0B800', fontWeight: 700, fontSize: 18, marginBottom: 12, textAlign: 'center' }}>
            YOU COMPLETED ALL 10 ROUNDS — YOU EARNED 2 FREE SQUARES!
          </div>
          <div style={{ background: '#111', border: '2px solid #F0B800', borderRadius: 12, padding: '24px 40px', marginBottom: 32, textAlign: 'left', minWidth: 320 }}>
            <div style={{ fontSize: 13, color: '#888', marginBottom: 16, letterSpacing: 2, textAlign: 'center' }}>SCORE BREAKDOWN</div>
            <ScoreBreakdown score={score} accentColor="#F0B800" />
          </div>
          <div style={{ fontSize: 14, marginBottom: 24, textAlign: 'center', lineHeight: 1.8 }}>
            <div style={{ color: '#F0B800', fontWeight: 700, fontSize: 15, marginBottom: 8 }}>
              🐔 You earned 1 free square for completing all 10 rounds!<br />
              Go pick your square for a chance to win!
            </div>
            <div style={{ color: '#F0B800', marginBottom: 4 }}>
              🎊 DON'T FORGET! The TOP 100 Chicken Droppings scores qualify for the PROGRESSIVE BONANZA Chicken Drop!
            </div>
            <span style={{ color: '#4CAF50', fontSize: 12 }}>Your score has been submitted to the leaderboard. Only top 5 scores per wallet are registered.</span>
          </div>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: '#FFFFFF', marginBottom: 8 }}>
            Auto-redirecting to pick your square in {autoRedirectTimer}s
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={() => navigate(selectedGameType ? GAME_CONFIGS[selectedGameType].route : '/drop/mega', { state: { pickSquare: true, wallet: effectivePublicKey, squaresEarned: 1 } })}
              style={{ background: '#F0B800', color: '#000', border: 'none', borderRadius: 8, padding: '12px 32px', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}
            >
              NOW GO PICK YOUR SQUARES AND WIN!! 🐔
            </button>
          </div>
        </div>
        <div style={{ background: '#111', border: '1px solid #2a2a2a', borderRadius: 12, padding: '20px', minWidth: 200, maxWidth: 520 }}>
          <LeaderboardDisplay leaderboard={leaderboard} currentWallet={effectivePublicKey} currentScore={score.finalScore} />
        </div>
      </div>
    );
  }

  if (gameState === 'tryComplete') {
    return (
      <div style={{ minHeight: '100vh', background: '#0a0a0a', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: 'sans-serif', color: 'white', textAlign: 'center', padding: '40px 20px' }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>🐔</div>
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 48, color: '#F0B800', letterSpacing: 4, marginBottom: 8 }}>THANKS FOR TRYING!</div>
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 16, color: '#aaa', marginBottom: 32, lineHeight: 1.8 }}>
          Connect your Solana wallet to play for real,<br />
          earn your square and win the Chicken Drop pool prize!
        </div>
        <div style={{ background: '#111', border: '2px solid #F0B800', borderRadius: 12, padding: '24px 40px', marginBottom: 32, minWidth: 320 }}>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, color: '#888', marginBottom: 16, textAlign: 'center' }}>YOUR TRIAL STATS (not saved)</div>
          <ScoreBreakdown score={score} accentColor="#F0B800" />
        </div>
        <WalletMultiButton />
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, color: '#F0B800', marginTop: 20, marginBottom: 8, fontWeight: 700 }}>
          Connect your Solana wallet to play!
        </div>
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: '#888', marginTop: 16 }}>
          Redirecting back to Chicken Droppings home in {tryRedirectTimer}s
        </div>
        <button
          onClick={() => {
            setIsTryMode(true);
            setPaid(true);
            setGameState('playing');
            startGame();
          }}
          style={{
            background: '#F0B800',
            color: '#000',
            border: 'none',
            borderRadius: '8px',
            padding: '12px 32px',
            fontSize: '14px',
            fontWeight: 700,
            fontFamily: "'DM Mono', monospace",
            cursor: 'pointer',
            letterSpacing: '1px',
            marginTop: '16px',
          }}
        >
          🎮 Practice Again
        </button>
        <button
          onClick={() => { stopMusic(); setIsTryMode(false); setGameState('intro'); setPaid(false); }}
          style={{ marginTop: 16, background: 'none', border: 'none', color: '#777', fontFamily: "'DM Mono', monospace", fontSize: 11, cursor: 'pointer' }}
        >
          go back now
        </button>
      </div>
    );
  }

  // ── PLAYING ──
  const liveAccuracy = gameRef.current.shots > 0
    ? Math.round((gameRef.current.hits / gameRef.current.shots) * 100)
    : 0;

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', userSelect: 'none' }}>
      {/* HUD */}

      <div style={{ textAlign: 'center', padding: '4px 0', background: '#0a0a0a', borderBottom: '1px solid #1a1a1a' }}>
        <span style={{ fontSize: 20 }}>🐔</span>
        <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '18px', color: '#F0B800', letterSpacing: '3px', margin: '0 10px' }}>
          CHICKEN DROPPINGS
        </span>
        <span style={{ fontSize: 20 }}>🐔</span>
      </div>
      <div style={{ display: 'flex', gap: 24, marginBottom: 12, fontFamily: 'sans-serif', alignItems: 'center' }}>
        <Link to="/" style={{ color: '#666', fontSize: 12, textDecoration: 'none' }}>← Exit</Link>
        <span style={{ color: '#F0B800', fontWeight: 700, fontSize: 14 }}>ROUND {currentRound + 1}/10</span>
        <span style={{ color: '#aaa', fontSize: 13 }}>{roundConfig.label}</span>
        <span style={{ color: '#5AAAE0', fontSize: 13 }}>⏱ {roundTime}s</span>
        <span style={{ color: '#4CAF50', fontSize: 13 }}>🎯 {liveAccuracy}%</span>
        <span style={{ color: '#888', fontSize: 13 }}>
          {gameRef.current.weaponsThrown}/{roundConfig.count} thrown
        </span>
      </div>

      {/* Game canvas */}
      <div style={{ position: 'relative' }}>
      {roundStartMessage && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'rgba(0,0,0,0.85)',
          border: '3px solid #F0B800',
          borderRadius: 12,
          padding: '20px 32px',
          textAlign: 'center',
          zIndex: 999,
          animation: 'flashIn 0.3s ease-out',
          pointerEvents: 'none',
          width: 'max-content',
          maxWidth: '80%',
        }}>
          <div style={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: 22,
            color: '#F0B800',
            letterSpacing: 3,
            lineHeight: 1.4,
          }}>
            ⚠️ TO WIN THE ROUND YOU CAN'T LET THE SUPER WEAPON REACH THE COOP!! ⚠️
          </div>
        </div>
      )}
      {gameState === 'playing' && showStartOverlay && (
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.45)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 100, borderRadius: 8 }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 48, color: '#F0B800', letterSpacing: 4, marginBottom: 8, textShadow: '0 0 30px rgba(240,184,0,0.8)' }}>
            READY?
          </div>
          <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, color: '#888', marginBottom: 24 }}>
            Round {currentRound + 1} — {ROUNDS[currentRound]?.label}
          </div>
          <button
            onClick={() => { gameRef.current.showStartOverlay = false; setShowStartOverlay(false); setGameReady(true); }}
            style={{ background: '#F0B800', color: '#000', border: 'none', borderRadius: 10, padding: '16px 48px', fontSize: 20, fontWeight: 900, cursor: 'pointer', fontFamily: "'Bebas Neue', sans-serif", letterSpacing: 3, boxShadow: '0 0 30px rgba(240,184,0,0.5)' }}
          >
            START GAME
          </button>
          <div style={{ marginTop: 20, textAlign: 'center' }}>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '12px', color: '#888', marginBottom: 6 }}>
              game starts automatically in
            </div>
            <div style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: '64px',
              color: '#F0B800',
              lineHeight: 1,
              animation: 'countdownFlash 0.5s ease infinite alternate',
              textShadow: '0 0 20px rgba(240,184,0,0.8)',
            }}>
              {startCountdown}
            </div>
          </div>
        </div>
      )}
      <svg
        viewBox={`0 0 ${GAME_W} ${GAME_H}`}
        style={{
          width: isMobile ? '100vw' : '100%',
          maxWidth: isMobile ? 'none' : 900,
          height: isMobile ? 'auto' : GAME_H + 20,
          background: '#1a2a1a',
          borderRadius: isMobile ? 0 : 12,
          border: '2px solid #2a3a2a',
          outline: 'none',
          cursor: 'none',
        }}
        tabIndex={0}
      >
        <style>{`
          @keyframes bonusPulse {
            from { opacity: 0.75; }
            to { opacity: 1; }
          }
          @keyframes countdownFlash {
            from { opacity: 0.4; transform: scale(0.95); }
            to { opacity: 1; transform: scale(1.05); }
          }
          @keyframes foxBonusFade {
            0% { opacity: 1; transform: translateY(0px); }
            100% { opacity: 0; transform: translateY(-40px); }
          }
          @keyframes flashIn {
            from { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
            to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          }
          @keyframes panSpin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @keyframes hawkFlap {
            from { transform: rotate(-25deg); }
            to { transform: rotate(15deg); }
          }
        `}</style>
        {/* Sky gradient */}
        <rect width={GAME_W} height={GAME_H * 0.6} fill="#1a3a1a" />
        {/* Ground */}
        <rect y={GAME_H - 40} width={GAME_W} height="40" fill="#2d4a1d" />
        <rect y={GAME_H - 42} width={GAME_W} height="4" fill="#3a6a28" />

        {/* Rooster boundary line */}
        <line x1="0" y1={ROOSTER_Y + 50} x2={GAME_W} y2={ROOSTER_Y + 50} stroke="#E05A5A" strokeWidth="1" strokeDasharray="8,6" opacity="0.4" />

        {/* Coop backdrop */}
        <Coop />

        {/* Farmer */}
        <Farmer x={farmerX} facing={farmerFacing} />

        {/* Rooster(s) */}
        {chickenCount === 1 && <Rooster x={roosterX} pooping={pooping} />}
        {chickenCount >= 2 && (
          <>
            <Rooster x={roosterX - 20} pooping={pooping} />
            <Rooster x={roosterX + 20} pooping={pooping} />
          </>
        )}

        {/* Weapons */}
        {weapons.filter(w => w.active).map(w => (
          <Weapon key={w.id} x={w.x} y={w.y} type={w.type} rotation={w.rotation} size={w.size || 60} />
        ))}

        {/* Super Weapons */}
        {superWeapons.filter(s => !s.hit).map(s => {
          const half = s.size / 2;
          return (
            <g key={s.id} transform={`translate(${s.x - half}, ${s.y - half})`}>
              <circle cx={half} cy={half} r={50} fill="rgba(240,50,50,0.15)" />
              <circle cx={half} cy={half} r={40} fill="rgba(240,50,50,0.2)" />
              <circle cx={half} cy={half} r={30} fill="rgba(240,50,50,0.1)" />
              <Weapon type={s.type} x={half} y={half} rotation={0} size={s.size} />
              <text x={half} y={-10} textAnchor="middle" fontSize="14" fill="#E05A5A" fontFamily="sans-serif" fontWeight="bold">⚠ SUPER WEAPON!</text>
            </g>
          );
        })}

        {/* Baby Farmers */}
        {babyFarmers.filter(b => b.alive).map(b => (
          <BabyFarmer key={b.id} x={b.x} />
        ))}

        {/* Farmer's Wife */}
        {farmersWife && (
          <FarmersWife x={farmersWife.x} facing="left" />
        )}

        {/* Fox */}
        {fox && (
          <image
            href="/images/weapons/fox.png"
            x={fox.x - 30}
            y={fox.y - 20}
            width={60}
            height={40}
            style={{ imageRendering: 'pixelated' }}
          />
        )}

        {/* Fox bonus text */}
        {foxBonusText && (
          <g style={{ animation: 'foxBonusFade 1.5s ease forwards' }}>
            <text x={foxBonusText.x} y={foxBonusText.y - 10} fill="#FF6B35" fontSize="20" fontWeight="bold" textAnchor="middle" fontFamily="sans-serif">
              🦊 +5000!
            </text>
          </g>
        )}

        {/* Virus bonus text */}
        {virusBonusText && (
          <g style={{ animation: 'foxBonusFade 1.5s ease forwards' }}>
            <text x={virusBonusText.x} y={virusBonusText.y - 10} fill="#00C853" fontSize="18" fontWeight="bold" textAnchor="middle" fontFamily="sans-serif">
              🦠 +2,500!
            </text>
          </g>
        )}

        {/* Virus escape text */}
        {virusEscapeText && (
          <g style={{ animation: 'foxBonusFade 1.5s ease forwards' }}>
            <text x={virusEscapeText.x} y={virusEscapeText.y + 20} fill="#ff4444" fontSize="18" fontWeight="bold" textAnchor="middle" fontFamily="sans-serif">
              🦠 -2,500!
            </text>
          </g>
        )}

        {/* Frying pan bonus text */}
        {fryingPanBonusText && (
          <g style={{ animation: 'foxBonusFade 1.5s ease forwards' }}>
            <text x={fryingPanBonusText.x} y={fryingPanBonusText.y - 10} fill="#FF8FB1" fontSize="20" fontWeight="bold" textAnchor="middle" fontFamily="sans-serif">
              🍳 +5,000!
            </text>
          </g>
        )}

        {/* Frying pan escape text */}
        {fryingPanEscapeText && (
          <g style={{ animation: 'foxBonusFade 1.5s ease forwards' }}>
            <text x={fryingPanEscapeText.x} y={fryingPanEscapeText.y + 20} fill="#ff4444" fontSize="20" fontWeight="bold" textAnchor="middle" fontFamily="sans-serif">
              🍳 -5,000!
            </text>
          </g>
        )}

        {/* Super Hawk bonus text */}
        {hawkBonusText && (
          <g style={{ animation: 'foxBonusFade 1.5s ease forwards' }}>
            <text x={hawkBonusText.x} y={hawkBonusText.y - 10} fill="#FFA500" fontSize="22" fontWeight="bold" textAnchor="middle" fontFamily="sans-serif">
              🦅 +5,000!
            </text>
          </g>
        )}

        {/* Super Hawk escape text */}
        {hawkEscapeText && (
          <g style={{ animation: 'foxBonusFade 1.5s ease forwards' }}>
            <text x={hawkEscapeText.x} y={hawkEscapeText.y + 20} fill="#ff4444" fontSize="22" fontWeight="bold" textAnchor="middle" fontFamily="sans-serif">
              🦅 -5,000!
            </text>
          </g>
        )}

        {/* Viruses */}
        {viruses.map(v => (
          <image
            key={v.id}
            href="/images/weapons/virus.png"
            x={v.x - 25}
            y={v.y - 25}
            width={50}
            height={50}
            style={{ imageRendering: 'pixelated' }}
          />
        ))}

        {/* Frying Pans */}
        {fryingPans.map(p => (
          <FryingPan key={p.id} x={p.x} y={p.y} size={56} />
        ))}

        {/* Super Hawks */}
        {hawks.map(h => (
          <Hawk key={h.id} x={h.x} y={h.y} facing={h.facing} />
        ))}

        {/* Poops */}
        {poops.map(p => (
          <Poop key={p.id} x={p.x} y={p.y} />
        ))}

        {/* Explosions */}
        {explosions.map(e => (
          <Explosion key={e.id} x={e.x} y={e.y} />
        ))}

        {/* Bonus flashes */}
        {bonusFlashes.map(f => (
          <text key={f.id} x={f.x} y={f.y} textAnchor="middle" fontSize="14" fill={f.color || '#FFD700'} fontFamily="sans-serif" fontWeight="bold">{f.text || '+500!'}</text>
        ))}

        {/* Baby bonus banner */}
        {showBabyBonus && (
          <g style={{ animation: 'bonusPulse 0.5s ease infinite alternate' }}>
            <rect x={0} y={FARMER_Y + 40} width={GAME_W} height={36} fill="#F0B800" opacity={0.45} rx={4} />
            <text x={GAME_W / 2} y={FARMER_Y + 64} textAnchor="middle" fontFamily="'Bebas Neue', sans-serif" fontSize={22} fill="#000" letterSpacing={3}>
              💩 BONUS! POOP ON THE BABY FARMERS FOR +500 PTS! 💩
            </text>
          </g>
        )}

        {/* Virus warning banner */}
        {virusWarning && (
          <g style={{ animation: 'bonusPulse 0.5s ease infinite alternate' }}>
            <rect x={0} y={FARMER_Y + 40} width={GAME_W} height={36} fill="rgba(180,0,0,0.85)" stroke="#ff4444" strokeWidth={2} rx={4} />
            <text x={GAME_W / 2} y={FARMER_Y + 62} textAnchor="middle" fontFamily="'Bebas Neue', sans-serif" fontSize={16} fill="#fff" letterSpacing={2}>
              🦠 WATCH OUT — BIRD FLU VIRUS!! Shoot it +2,500 pts · Miss it -2,500 pts!
            </text>
          </g>
        )}

        {/* Farmer's Wife warning banner */}
        {wifeWarning && (
          <g style={{ animation: 'bonusPulse 0.5s ease infinite alternate' }}>
            <rect x={0} y={FARMER_Y + 40} width={GAME_W} height={36} fill="rgba(217,74,138,0.85)" stroke="#FF8FB1" strokeWidth={2} rx={4} />
            <text x={GAME_W / 2} y={FARMER_Y + 62} textAnchor="middle" fontFamily="'Bebas Neue', sans-serif" fontSize={16} fill="#fff" letterSpacing={2}>
              👩‍🌾 FARMER'S WIFE!! Shoot the frying pan +5,000 · Miss it -5,000!
            </text>
          </g>
        )}

        {/* Super Hawk warning banner */}
        {hawkWarning && (
          <g style={{ animation: 'bonusPulse 0.5s ease infinite alternate' }}>
            <rect x={0} y={FARMER_Y + 40} width={GAME_W} height={36} fill="rgba(140,70,15,0.9)" stroke="#FFA500" strokeWidth={2} rx={4} />
            <text x={GAME_W / 2} y={FARMER_Y + 62} textAnchor="middle" fontFamily="'Bebas Neue', sans-serif" fontSize={16} fill="#fff" letterSpacing={2}>
              🦅 SUPER HAWKS!! Hit both +5,000 each · Miss one -5,000!
            </text>
          </g>
        )}

        {/* Double Chicken status message */}
        {chickenMessage && (
          <g style={{ animation: 'bonusPulse 0.4s ease infinite alternate' }}>
            <rect x={GAME_W / 2 - 200} y={GAME_H / 2 - 30} width={400} height={48} fill={chickenMessage.includes('LOST') ? 'rgba(180,40,40,0.92)' : 'rgba(50,140,50,0.92)'} stroke={chickenMessage.includes('LOST') ? '#ff4444' : '#4CAF50'} strokeWidth={2} rx={6} />
            <text x={GAME_W / 2} y={GAME_H / 2 + 2} textAnchor="middle" fontFamily="'Bebas Neue', sans-serif" fontSize={22} fill="#fff" letterSpacing={3}>
              {chickenMessage}
            </text>
          </g>
        )}

        {/* Round progress bar */}
        <rect x="10" y="10" width="200" height="8" fill="#333" rx="4" />
        <rect x="10" y="10" width={200 * (gameRef.current.weaponsThrown / roundConfig.count)} height="8" fill="#F0B800" rx="4" />
        <text x="10" y="28" fontSize="9" fill="#888" fontFamily="sans-serif">
          ROUND PROGRESS
        </text>
        <text x="10" y="42" fontFamily="'DM Mono', monospace" fontSize="11" fill="#F0B800" fontWeight="bold">
          SCORE: {liveScore.toLocaleString()}
        </text>
      </svg>
      </div>

      {isMobile && gameState === 'playing' && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12, width: '100%', maxWidth: 900, paddingLeft: 8, paddingRight: 8, boxSizing: 'border-box' }}>
          <button
            ref={leftBtnRef}
            onMouseDown={() => { gameRef.current.keys['ArrowLeft'] = true; }}
            onMouseUp={() => { gameRef.current.keys['ArrowLeft'] = false; }}
            onMouseLeave={() => { gameRef.current.keys['ArrowLeft'] = false; }}
            style={{
              flex: 1, height: 80, background: '#1a2a1a', border: '2px solid #F0B800',
              borderRadius: 12, color: '#F0B800', fontSize: 38, fontWeight: 900,
              fontFamily: 'sans-serif', userSelect: 'none', touchAction: 'manipulation',
              cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
            }}
            aria-label="Move left"
          >
            ←
          </button>
          <button
            ref={shootBtnRef}
            onMouseDown={() => { gameRef.current.keys[' '] = true; }}
            onMouseUp={() => { gameRef.current.keys[' '] = false; }}
            onMouseLeave={() => { gameRef.current.keys[' '] = false; }}
            style={{
              flex: 1, height: 80, background: '#1a2a1a', border: '2px solid #F0B800',
              borderRadius: 12, color: '#F0B800', fontSize: 34, fontWeight: 900,
              fontFamily: 'sans-serif', userSelect: 'none', touchAction: 'manipulation',
              cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
            }}
            aria-label="Shoot"
          >
            💩
          </button>
          <button
            ref={rightBtnRef}
            onMouseDown={() => { gameRef.current.keys['ArrowRight'] = true; }}
            onMouseUp={() => { gameRef.current.keys['ArrowRight'] = false; }}
            onMouseLeave={() => { gameRef.current.keys['ArrowRight'] = false; }}
            style={{
              flex: 1, height: 80, background: '#1a2a1a', border: '2px solid #F0B800',
              borderRadius: 12, color: '#F0B800', fontSize: 38, fontWeight: 900,
              fontFamily: 'sans-serif', userSelect: 'none', touchAction: 'manipulation',
              cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
            }}
            aria-label="Move right"
          >
            →
          </button>
        </div>
      )}

      <div style={{ marginTop: 10, color: '#999', fontSize: 12, fontFamily: 'sans-serif' }}>
        ← → Move Rooster &nbsp;&nbsp; SPACEBAR = Super Stool (tap to blast!)
      </div>
    </div>
  );
}
